// kernel/useGenerationTasks.ts
import { generatedImageDimensions, shouldPreserveGeneratedProportions } from '#lib/generatedImageDimensions';
import { useCallback, useEffect, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import { toast } from 'sonner';
import { isTerminalImageGenerationFailure, terminalImageGenerationFailureCode, readImageGenerationFailureDetails } from '#lib/geminiImageFailure';
import { sanitizeUserFacingError } from '#lib/failed-generation';
import { isGenerationTaskInFlight, isListTaskRecovering } from '#lib/list/listGenerationTelemetry';
import { GENERATION_NEVER_STARTED_MESSAGE, NEVER_STARTED_AFTER_MS } from '#lib/failed-generation';
import { listGenerationRecoveryStrategy } from '#lib/list/listGenerationRepair';
import { getCloudTmpUrl, uploadFileToCloud, uploadImageDataToCloud } from '#lib/azure-client';
import { buildCloudAssetEntry, upsertCloudAssets } from '#lib/cloudAssetRegistry';
import { LAYER_PIXELS_UPLOAD_FAILED, STUDIO_LAYER_DTO_AUTOSAVE_SCOPE, buildStudioLayerMutationPayload } from '#lib/studio-runtime-dto';
import { materializeProjectGenerationLayer } from '#lib/generation-output-layer';
import { adoptProjectGeneration } from '#lib/adopt-project-generation';
import {
  extractFramesFromVideo,
  MAX_COLOR_HARMONIZATION_ANALYSIS_PIXELS,
  resizeImageDataToPixelBudget,
} from '#lib/canvas-utils';
import { retry } from '#lib/jobs';
import { CloudAsset, GenerationChange, KernelHandle, Layer, Rect, GenerationTask, OriginalImage, useGenerationTasksReturnType, BaseParams, PendingTaskPromise, TaskCompletionData, DiffusionParams } from  '#types/types';
import { buildGridScript, findGridBatchAnchor, findGridBatchLayers, readGridBatch, readResumableGridBatch, type GridBatch } from '#lib/grid-generation-script';
import { v4 as uuidv4 } from 'uuid';
import { getSpecificErrorMessage } from '#lib/utils';
import { performColorHarmonization } from '#lib/color-harmonization';
import { generationHarmonizeApplies } from '#lib/layer-integration';
import cvReadyPromise from "@techstark/opencv-js";
import { getNextRenderOrderForWorkspace } from '#lib/layer-render-order';
import { hideBuriedLayersInVisibility } from '#lib/buried-layer-stack';
import { describeGeneratedResult, isGenericListOutputName, uniqueGeneratedResultName, type GeneratedResultKind } from '#lib/generated-result-name';
import { syncRasterLayerRectSize } from '#lib/layer-geometry';
import {
  SVG_LAYER_PARAM_KEY,
  loadSvgIntrinsicSizeFromUrl,
  parseSvgIntrinsicSize,
  resolveSvgLayerInlineMarkup,
} from '#lib/svg-layer';
import {
  resolvePendingVideoGenerationTrack,
  type PendingVideoGenerationTrack,
} from '#lib/timeline-generation-placeholders';
import {
  patchProjectRuntimeField,
  readProjectRuntimeField,
} from '#lib/project-runtime-layer-ui-state';
import type { GenerationTaskStatusSocketPayload } from '#lib/socket-events';
import { areGenerationOutputScopesEqual, normalizeGenerationOutputScope } from '#lib/generation-output-scope';
import { syncWorkspaceRuntimeVersionsFromInventory } from '@/kernel/useVersions';
import { buildMireloRecoveryCarrier, buildMireloSeparatedLayers } from '#lib/mirelo-sfx';
import { extractAudioFromVideo } from '#lib/audio-extract-client';
import { compactCompletedGenerationTask, compactTaskCompletionData, compactTaskCompletionLayer } from '#lib/generation-task-memory';
import { computeGenerationPostprocess, selectHighResMaskingLayers } from '#lib/generation-postprocess';
import { runGenerationPostprocessWithFallback } from '#lib/generation-postprocess-client';
import { isVectorOutputStudioModel } from '#lib/model-registry';
import { fitRectInside, isVectorModelId } from '#lib/vector-models';

interface UseGenerationTasksProps {
  kernel: React.RefObject<KernelHandle | null>;
}

const TASKS_POSTPROCESS_PERF_DEBUG = process.env.NEXT_PUBLIC_TASKS_POSTPROCESS_PERF_DEBUG === 'true';
const VIDEO_SOCKET_SUBMISSION_GRACE_MS = 30_000;
const VIDEO_SOCKET_TERMINAL_TIMEOUT_MS = 60 * 60 * 1000;
const createLayerId = (): string => `layer-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * A generated result's name, unique inside the workspace it lands in. A batch produces N
 * results from one prompt, so the bare label repeats; the agent resolves layers by name
 * (useLayers: `find(l => l.id === identifier || l.name === identifier)`), so duplicates
 * make it act on whichever came first.
 */
const nameGeneratedResult = (
  kernelHandle: KernelHandle | null,
  workspaceId: string | null | undefined,
  task: { params?: { prompt?: unknown } | null; model?: string | null },
  kind: GeneratedResultKind,
): string => {
  const desired = describeGeneratedResult(task, kind);
  const normalizedWorkspaceId = typeof workspaceId === 'string' ? workspaceId.trim() : '';
  if (!kernelHandle || !normalizedWorkspaceId) return desired;
  const taken = kernelHandle
    .getLayersInWorkspaceContext(normalizedWorkspaceId)
    .map((layer) => layer.name)
    .filter((name): name is string => typeof name === 'string' && name.length > 0);
  return uniqueGeneratedResultName(desired, taken);
};
const MIRELO_AUDIO_EXTRACT_TIMEOUT_MS = 10 * 60 * 1000;

type VideoSocketTerminalWaiter = {
  resolve: (payload: GenerationTaskStatusSocketPayload) => void;
  reject: (error: Error) => void;
  projectId: string | null;
  lastConfirmedAt: number;
  missingSince: number | null;
  missingCount: number;
};

const isGenerationTaskStatusSocketPayload = (payload: unknown): payload is GenerationTaskStatusSocketPayload => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
  const record = payload as Record<string, unknown>;
  return (
    typeof record.clientTaskId === 'string' &&
    (record.status === 'RUNNING' || record.status === 'SUCCEEDED' || record.status === 'FAILED') &&
    typeof record.createdAt === 'string' && Number.isFinite(Date.parse(record.createdAt)) &&
    ['projectId', 'chatSessionId', 'stepLabel'].every(key => record[key] == null || typeof record[key] === 'string')
  );
};

const normalizeSocketOutputUrls = (payload: GenerationTaskStatusSocketPayload): string[] => {
  const urls = Array.isArray(payload.outputUrls)
    ? payload.outputUrls.filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
    : [];
  if (urls.length > 0) return Array.from(new Set(urls.map((url) => url.trim())));
  return typeof payload.outputUrl === 'string' && payload.outputUrl.trim().length > 0
    ? [payload.outputUrl.trim()]
    : [];
};

const isLateVideoSocketBackedSubmissionError = (
  task: GenerationTask,
  error: unknown,
  elapsedMs: number
): boolean => {
  // Video AND magnific image upscale both run for minutes and deliver their result
  // over the socket, so both use this late / gateway-timeout socket fallback.
  const isSocketBackedTask = task.type === 'film' || task.model === 'magnific-image-upscale' || task.outputScope?.kind === 'PROJECT';
  if (!isSocketBackedTask || !axios.isAxiosError(error)) return false;
  if (error.response?.data?.outputRecoveryUnavailable === true) return false;
  const status = error.response?.status;
  if (typeof status === 'number' && status >= 400 && status < 499) return false;
  // The Azure front-end/proxy tears down our long-running film request well before
  // the provider finishes, and it does NOT always say 504: real incidents surface as
  // 502 Bad Gateway (seen live 2026-07-27: client declared "failed", agent relaunched
  // and double-spent while fal was still rendering; the finished video then orphaned
  // because no waiter was listening). Any gateway-layer 5xx after submission means
  // "the request died, not the job" -> fall back to the socket terminal event.
  // 499 is nginx's "client closed request", which the Azure front end surfaces as a real
  // response status rather than a transport error. It therefore slipped past BOTH the 5xx
  // list above and the `!error.response` fallback below, so the client declared the task
  // failed outright (seen live 2026-07-29: the agent narrated "timeout 499 error, relaunch
  // the generation" while fal was still rendering; the render finished at 493s and orphaned
  // because the task had already been torn down client-side). Same class as the 502 above:
  // the request died, the job did not.
  if (status === 499 || status === 502 || status === 503 || status === 504) return true;
  const message = `${error.message || ''} ${typeof error.response?.data === 'string' ? error.response.data : ''}`;
  if (/gateway\s*timeout|bad\s*gateway|service\s*unavailable|proxy\s*error|client\s*closed\s*request|499|50[234]/i.test(message)) return true;
  return !error.response && elapsedMs >= VIDEO_SOCKET_SUBMISSION_GRACE_MS;
};

// Crop an ImageData down to the bounding box of its non-transparent pixels. Returns the cropped
// data plus the pixel offset of the crop, or null if it's fully transparent or already tight.
// Used so a masked inpaint result becomes a layer the size of the painted patch, not the whole area.
const cropImageDataToOpaqueBounds = (
  src: ImageData,
): { data: ImageData; offsetX: number; offsetY: number } | null => {
  const { width, height, data } = src;
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if ((data[(y * width + x) * 4 + 3] ?? 0) > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX || maxY < minY) return null; // fully transparent
  const bw = maxX - minX + 1;
  const bh = maxY - minY + 1;
  if (bw >= width && bh >= height) return null; // already tight, nothing to crop
  const out = new ImageData(bw, bh);
  for (let y = 0; y < bh; y++) {
    for (let x = 0; x < bw; x++) {
      const si = ((y + minY) * width + (x + minX)) * 4;
      const di = (y * bw + x) * 4;
      out.data[di] = data[si] ?? 0;
      out.data[di + 1] = data[si + 1] ?? 0;
      out.data[di + 2] = data[si + 2] ?? 0;
      out.data[di + 3] = data[si + 3] ?? 0;
    }
  }
  return { data: out, offsetX: minX, offsetY: minY };
};
const normalizeGenerationTasks = (value: unknown): GenerationTask[] => (
  Array.isArray(value) ? (value as GenerationTask[]) : []
);
const normalizeTimelineString = (value: unknown): string => (
  typeof value === 'string' ? value.trim() : ''
);
const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};
const buildProjectTimelineSubjectKey = (projectId: string): string => `Project:${projectId}`;

const appendLayerModificationLog = (
  layer: Layer,
  entry: NonNullable<Layer['modificationLog']>[number]
): Layer['modificationLog'] => {
  const existing = Array.isArray(layer.modificationLog)
    ? layer.modificationLog.map((item) => ({ ...item }))
    : [];
  return [...existing, { ...entry }];
};

const extractKlingOmniReferenceResolutionError = (message: string): string | null => {
  if (typeof message !== 'string' || message.trim().length === 0) return null;
  const match = message.match(/Reference video resolution must be at least 720px per side\. Got \d+x\d+\.?/i);
  if (!match) return null;
  return match[0].trim();
};

const extractKlingOmniFriendlyError = (message: string): string | null => {
  if (typeof message !== 'string' || message.trim().length === 0) return null;

  const resolution = extractKlingOmniReferenceResolutionError(message);
  if (resolution) return resolution;

  const formatMatch = message.match(/Reference video must be \.mp4 or \.mov\. Got \.[a-z0-9]+\.?/i);
  if (formatMatch) return formatMatch[0].trim();

  const durationMatch = message.match(/Reference video must be at least [\d.]+ seconds\. Got [\d.]+s\.?/i);
  if (durationMatch) return durationMatch[0].trim();

  if (/reference_video must be an absolute http\(s\) uri/i.test(message) || /input\.reference_video: Does not match format 'uri'/i.test(message)) {
    return 'Reference video URL is invalid. Please retry so the app can prepare a valid public video URL.';
  }

  if (/Video editing is not supported with first\/tail image input/i.test(message)) {
    return 'This model does not allow start/end image inputs when a reference video is provided.';
  }

  return null;
};

const startPostprocessPerf = (task: GenerationTask, label: string) => {
  if (!TASKS_POSTPROCESS_PERF_DEBUG) return () => {};
  const now = typeof performance !== 'undefined' && performance.now ? () => performance.now() : () => Date.now();
  const startedAt = now();
  return () => {
    const elapsedMs = now() - startedAt;
    const taskId = task.id || 'unknown';
    console.log(`[TasksPostprocess] [${taskId}] ${label}: ${elapsedMs.toFixed(1)}ms`);
  };
};

const createTaskCancellationError = (taskId: string, reason = 'Generation task cancelled.'): Error => {
  const error = new Error(`Task ${taskId}: ${reason}`);
  error.name = 'TaskCancellationError';
  return error;
};

// Best effort: the server only reads image headers, so a damaged provider image is caught here or not at all.
const warnIfUndecodable = async (layers: Layer[], handleError: (message: string) => void) => {
  for (const layer of layers) {
    if (layer.type || !layer.imageSrc) continue;
    const image = new Image();
    if (typeof image.decode !== 'function') return;
    image.src = layer.imageSrc;
    try { await image.decode(); }
    catch { handleError('A generated image is damaged and could not be displayed. Delete it and generate again.'); }
  }
};

const isTaskCancellationError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { name?: string; code?: string };
  return candidate.name === 'TaskCancellationError' ||
    candidate.name === 'AbortError' ||
    candidate.name === 'CanceledError' ||
    candidate.code === 'ERR_CANCELED';
};

async function imageDataToBase64(
    input: ImageData | (OriginalImage | null | undefined),
    mimeType: 'image/png' | 'image/jpeg' = 'image/png',
    maxDimension?: number
): Promise<string> {
    let imageData: ImageData;
    let width: number;
    let height: number;

    if (input instanceof ImageData) {
        imageData = input;
        width = imageData.width;
        height = imageData.height;
    } else if (input && input.imageData && input.width && input.height) {
        imageData = input.imageData;
        width = input.width;
        height = input.height;
    } else {
        throw new Error('Invalid input: must be ImageData or a valid ImageLike object');
    }

    // 1. Calculate Target Dimensions
    let targetWidth = width;
    let targetHeight = height;

    if (maxDimension && (width > maxDimension || height > maxDimension)) {
        if (width > height) {
            targetHeight = Math.round(height * (maxDimension / width));
            targetWidth = maxDimension;
        } else {
            targetWidth = Math.round(width * (maxDimension / height));
            targetHeight = maxDimension;
        }
    }

    // 2. Create a temporary canvas to hold the original ImageData
    // (ImageData cannot be directly drawn scaled; it must be put 1:1 first)
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) throw new Error('Could not get temp canvas context');
    tempCtx.putImageData(imageData, 0, 0);

    // 3. Create the final canvas (possibly resized)
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context for Base64 conversion');

    // 4. Handle Transparency for JPEGs (fill white)
    if (mimeType === 'image/jpeg') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, targetWidth, targetHeight);
    }

    // 5. Draw and Scale
    // This draws the temp canvas onto the final canvas, handling the resizing
    ctx.drawImage(tempCanvas, 0, 0, targetWidth, targetHeight);

    return new Promise((resolve, reject) => {
        // Use 0.7 quality for JPEGs to save bandwidth
        const quality = mimeType === 'image/jpeg' ? 0.7 : undefined;
        
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Canvas toBlob failed for Base64 conversion'));
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result.split(',')[1] || '');
                } else {
                    reject(new Error('FileReader did not return a string for Base64 conversion'));
                }
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        }, mimeType, quality);
    });
}

const resolveTileOverlapFadeRadius = (task: GenerationTask): number => {
  const imageData = task.layer?.imageData;
  const rect = task.rect;
  const overlapScale = task.layerConsolidation?.tileOverlapScale;
  if (
    !imageData ||
    !(rect.w > 0 && rect.h > 0) ||
    typeof overlapScale !== 'number' ||
    !Number.isFinite(overlapScale) ||
    overlapScale <= 1
  ) {
    return 0;
  }

  // task.rect is already the expanded generation zone; recover the original tile margin from it.
  const marginRatio = (overlapScale - 1) / (2 * overlapScale);
  const pixelsPerWorldX = imageData.width / rect.w;
  const pixelsPerWorldY = imageData.height / rect.h;
  const marginPixels = Math.min(
    rect.w * marginRatio * pixelsPerWorldX,
    rect.h * marginRatio * pixelsPerWorldY,
  );
  return Number.isFinite(marginPixels) ? Math.max(0, Math.round(marginPixels)) : 0;
};

const finalizeGenerationTaskSnapshot = async (
  generationTaskId: string | null | undefined,
  fallbackClientTaskId: string,
  finalAssetUrls: string[],
  options?: {
    metadata?: Record<string, unknown> | null;
    candidateVersionId?: string | null;
    chatSessionId?: string | null;
    outputScope?: GenerationTask['outputScope'] | null;
    // Legacy transport fields remain accepted; the server never uses them as a destination.
    workspaceId?: string | null;
    projectId?: string | null;
    layerId?: string | null;
    generationLogId?: string | null;
    providerJobId?: string | null;
    taskStatus?: string | null;
    failureMessage?: string | null;
  }
) => {
  const identifier = generationTaskId ?? fallbackClientTaskId;
  const taskStatus =
    typeof options?.taskStatus === 'string' && options.taskStatus.trim().length > 0
      ? options.taskStatus.trim().toUpperCase()
      : null;
  if (!identifier || (finalAssetUrls.length === 0 && !taskStatus)) {
    return;
  }

  try {
    await fetch(`/api/generation-tasks/${encodeURIComponent(identifier)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        finalAssetUrls,
        finalMetadata: options?.metadata ?? null,
        candidateVersionId: options?.candidateVersionId ?? null,
        clientTaskId: fallbackClientTaskId,
        chatSessionId: options?.chatSessionId ?? null,
        outputScope: options?.outputScope ?? normalizeGenerationOutputScope(undefined, {
          workspaceId: options?.workspaceId,
          projectId: options?.projectId,
        }),
        // These fields are retained for old clients; the PATCH route resolves the persisted scope.
        layerId: options?.layerId ?? null,
        generationLogId: options?.generationLogId ?? null,
        providerJobId: options?.providerJobId ?? null,
        taskStatus: taskStatus ?? undefined,
        failureMessage: options?.failureMessage ?? null,
      }),
    });
  } catch (error) {
    console.error('[useGenerationTasks] Failed to finalize generation task snapshot', error);
  }
};

// Sends these changes now on the layer's own save; a flush another caller started first may carry them, so it waits for that one too. True once written.
const saveLayerNow = async (k: KernelHandle, layerId: string, changes: Parameters<KernelHandle['queueLayerSave']>[1]): Promise<boolean> => {
  k.queueLayerSave(layerId, changes);
  for (;;) {
    await k.awaitAutosaveInFlight('StudioLayer', layerId, STUDIO_LAYER_DTO_AUTOSAVE_SCOPE);
    if (await k.flushAutosave('StudioLayer', layerId, STUDIO_LAYER_DTO_AUTOSAVE_SCOPE)) return true;
    const { saveState } = k.getAutosaveState('StudioLayer', layerId, STUDIO_LAYER_DTO_AUTOSAVE_SCOPE);
    if (saveState !== 'saving') return saveState === 'saved';
  }
};

const useGenerationTasks = ({
  kernel
}: UseGenerationTasksProps) : useGenerationTasksReturnType => {
  const finalizeRasterLayerGeometry = useCallback((layer: Layer, intrinsicSize?: { width: number; height: number }): Layer => {
    return syncRasterLayerRectSize(layer, intrinsicSize);
  }, []);
  const pendingTaskPromisesRef = useRef<Map<string, PendingTaskPromise>>(new Map());
  const videoSocketTerminalEventsByTaskIdRef = useRef<Map<string, GenerationTaskStatusSocketPayload>>(new Map());
  const recoverCompletedFilmTaskRef = useRef<((task: GenerationTask, payload: GenerationTaskStatusSocketPayload) => void) | null>(null);
  const videoSocketTerminalWaitersByTaskIdRef = useRef<Map<string, VideoSocketTerminalWaiter>>(new Map());
  const generationReconciliationInFlightRef = useRef(new Set<string>());
  const generationDiscoveryInFlightRef = useRef(new Map<string, AbortSignal>());
  const cancelledGenerationTaskIdsRef = useRef(new Set<string>());
  const generationRestoreVersionRef = useRef(0);
  const generationStoppedAtBySessionRef = useRef(new Map<string, number>());
  const layerPersistenceQueueRef = useRef<Map<string, Promise<void>>>(new Map());
  const generationSpawnTimelineEventIdByTaskIdRef = useRef<Map<string, string>>(new Map());
  const handledGenerationTaskIdsRef = useRef<Set<string>>(new Set());

  const getGenerationTasks = useCallback<KernelHandle['getGenerationTasks']>((projectIdInput) => {
    const k = kernel.current;
    if (!k) return [];
    return readProjectRuntimeField(
      k,
      projectIdInput,
      'generationTasks',
      [],
      normalizeGenerationTasks
    );
  }, [kernel]);

  const setGenerationTasks = useCallback<KernelHandle['setGenerationTasks']>((projectIdInput, updater) => {
    const k = kernel.current;
    if (!k) return;
    patchProjectRuntimeField(
      k,
      projectIdInput,
      'generationTasks',
      updater,
      [],
      normalizeGenerationTasks
    );
  }, [kernel]);

  const findGenerationTaskRuntimeMetadata = useCallback((taskId: string): {
    task: GenerationTask | null;
    projectId: string | null;
    chatSessionId: string | null;
  } => {
    const k = kernel.current;
    if (!k) return { task: null, projectId: null, chatSessionId: null };

    const projectIds = new Set<string>();
    const activeProjectId = k.resolveActiveSessionProjectId?.();
    if (typeof activeProjectId === 'string' && activeProjectId.trim().length > 0) {
      projectIds.add(activeProjectId.trim());
    }
    Object.keys(k.getInventory().Project.registry).forEach((projectId) => {
      if (projectId.trim().length > 0) projectIds.add(projectId.trim());
    });

    for (const projectId of projectIds) {
      const task = getGenerationTasks(projectId).find((candidate) => candidate.id === taskId);
      if (!task) continue;
      const chatSessionId =
        typeof task.chatSessionId === 'string' && task.chatSessionId.trim().length > 0
          ? task.chatSessionId.trim()
          : null;
      return { task, projectId, chatSessionId };
    }

    return { task: null, projectId: null, chatSessionId: null };
  }, [kernel, getGenerationTasks]);

  const handleGenerationTaskStatusSocketEvent = useCallback<KernelHandle['handleGenerationTaskStatusSocketEvent']>((payload) => {
    if (!isGenerationTaskStatusSocketPayload(payload)) return;
    const clientTaskId = payload.clientTaskId.trim();
    if (!clientTaskId) return;
    if (cancelledGenerationTaskIdsRef.current.has(clientTaskId)) return;

    const payloadProjectId = typeof payload.projectId === 'string' ? payload.projectId.trim() : '';
    const { task: existingTask, projectId: fallbackProjectId } = findGenerationTaskRuntimeMetadata(clientTaskId);
    const projectId = payloadProjectId || fallbackProjectId || '';
    if (existingTask?.projectId && payloadProjectId && existingTask.projectId !== payloadProjectId) return;
    if (existingTask?.chatSessionId && payload.chatSessionId && existingTask.chatSessionId !== payload.chatSessionId) return;
    if (existingTask?.outputScope && payload.outputScope && !areGenerationOutputScopesEqual(existingTask.outputScope, payload.outputScope)) return;
    const waiter = videoSocketTerminalWaitersByTaskIdRef.current.get(clientTaskId);
    if (waiter?.projectId && payloadProjectId && waiter.projectId !== payloadProjectId) return;
    if (waiter) {
      waiter.lastConfirmedAt = Date.now();
      waiter.missingSince = null;
      waiter.missingCount = 0;
    }

    if (projectId) {
      setGenerationTasks(projectId, (previousTasks) => previousTasks.map((task) => {
        if (task.id !== clientTaskId) return task;
        if (task.isOutcomeReported || (task.error && !task.recoveryPending)) return task;
        return {
          ...task,
          serverTaskId:
            typeof payload.generationTaskId === 'string' && payload.generationTaskId.trim().length > 0
              ? payload.generationTaskId.trim()
              : task.serverTaskId,
          outputScope: payload.outputScope ?? task.outputScope,
          ...(payloadProjectId === task.projectId ? { lastStatusConfirmedAt: Date.now() } : {}),
          ...(task.recoveryPending && payload.status === 'RUNNING' ? { error: 'Generation status is unconfirmed. Check status before trying again.' } : {}),
          // video-loop plan Phase 2/3: carry the live self-validation tier + ETA so
          // the composer telemetry can name the running stage ("Motion check" /
          // "Final render") and show a bounded countdown.
          loopTier: payload.loopTier ?? task.loopTier,
          loopStepLabel: payload.status === 'SUCCEEDED' ? 'Saved result; loading output' : payload.stepLabel ?? task.loopStepLabel,
          loopEtaSeconds: typeof payload.etaSeconds === 'number' ? payload.etaSeconds : task.loopEtaSeconds,
          loopProgress: typeof payload.progress === 'number' ? payload.progress : task.loopProgress,
        };
      }));
    }

    if (payload.status === 'RUNNING') return;

    if (waiter) {
      waiter.resolve(payload);
      videoSocketTerminalWaitersByTaskIdRef.current.delete(clientTaskId);
      return;
    }
    if (handledGenerationTaskIdsRef.current.has(clientTaskId) && !existingTask?.isOutcomeReported && !existingTask?.error) {
      videoSocketTerminalEventsByTaskIdRef.current.set(clientTaskId, payload);
      return;
    }
    if (existingTask?.isOutcomeReported || (existingTask?.error && !existingTask.recoveryPending)) {
      videoSocketTerminalEventsByTaskIdRef.current.delete(clientTaskId);
      return;
    }

    // List can render persisted runtime tasks without an active waitForTask promise
    // (reload, socket loss, or a previous deploy in the middle of a generation).
    // A reconciled success is only complete once its persisted version is in the
    // runtime workspace. Keep the pending tile visible until that hydration finishes,
    // so the UI never passes through a state containing neither pending nor output.
    if (projectId) {
      if (payload.status === 'SUCCEEDED') {
        if (payload.outputScope?.kind === 'PROJECT' && payload.outputScope.projectId === projectId) {
          const kernelHandle = kernel.current;
          if (!kernelHandle) return;
          const task = existingTask ?? { id: clientTaskId, projectId, outputScope: payload.outputScope } as GenerationTask;
          void adoptProjectGeneration(kernelHandle, task, payload).then(() => {
            setGenerationTasks(projectId, tasks => tasks.map(item => item.id === clientTaskId
              ? compactCompletedGenerationTask({ ...item, isOutcomeReported: true, error: undefined, recoveryPending: false, urls: normalizeSocketOutputUrls(payload) })
              : item));
            videoSocketTerminalEventsByTaskIdRef.current.delete(clientTaskId);
          }).catch(() => {
            kernelHandle.handleError('The generated result is saved but could not be loaded. Reconnect or refresh to load it; do not generate again.');
          });
          return;
        }
        const candidateVersionId = typeof payload.candidateVersionId === 'string'
          ? payload.candidateVersionId.trim()
          : '';
        const workspaceId = typeof payload.workspaceId === 'string'
          ? payload.workspaceId.trim()
          : (existingTask?.workspaceId ?? '').trim();
        if (candidateVersionId && workspaceId) {
          void (async () => {
            const kernelHandle = kernel.current;
            if (!kernelHandle) return;
            const workspace = await kernelHandle.syncEntity('StudioWorkspace', workspaceId, { force: true });
            const version = await kernelHandle.syncEntity('StudioWorkspaceVersion', candidateVersionId, { force: true });
            const layerId = typeof payload.layerId === 'string' ? payload.layerId.trim() : '';
            if (layerId) {
              await kernelHandle.syncEntity('StudioLayer', layerId, { force: true });
            }
            if (!workspace || !version) return;
            syncWorkspaceRuntimeVersionsFromInventory({ kernelHandle, workspaceId });
            if (!kernelHandle.getWorkspace(workspaceId)?.versions?.some((candidate) => candidate.id === candidateVersionId)) return;
            const urls = normalizeSocketOutputUrls(payload);
            setGenerationTasks(projectId, (previousTasks) => previousTasks.map((task) => {
              if (task.id !== clientTaskId || task.isOutcomeReported || task.error) return task;
              return compactCompletedGenerationTask({
                ...task,
                serverTaskId: typeof payload.generationTaskId === 'string' && payload.generationTaskId.trim()
                  ? payload.generationTaskId.trim()
                  : task.serverTaskId,
                outputScope: payload.outputScope ?? task.outputScope,
                isOutcomeReported: true,
                urls: urls.length > 0 ? urls : task.urls,
              });
            }));
          })().catch((error) => {
            console.warn('[useGenerationTasks] Failed to hydrate reconciled output version.', error);
          });
          return;
        }
        if (existingTask) {
          const urls = normalizeSocketOutputUrls(payload);
          const isProjectOutput = payload.outputScope?.kind === 'PROJECT' || existingTask.outputScope?.kind === 'PROJECT';
          if (urls.length > 0 && (payload.type === 'film' || existingTask?.type === 'film' || isProjectOutput)) {
            const recover = recoverCompletedFilmTaskRef.current;
            if (recover) {
              recover(
                payload.outputScope ? { ...existingTask, outputScope: payload.outputScope } : existingTask,
                payload,
              );
              return;
            }
          }
        }
      } else {
        if (existingTask && !existingTask.isOutcomeReported && (!existingTask.error || existingTask.recoveryPending) && isTerminalImageGenerationFailure(payload)
          && !(existingTask.listAttempt && listGenerationRecoveryStrategy(payload))) {
          kernel.current?.handleError(payload.errorMessage || 'The model did not produce an image.', {
            id: `generation-failure-${clientTaskId}`, logToConsole: false,
          });
        }
        setGenerationTasks(projectId, (previousTasks) => previousTasks.map((task) => {
          if (task.id !== clientTaskId || task.isOutcomeReported || (task.error && !task.recoveryPending)) return task;
          return {
            ...compactCompletedGenerationTask({
              ...task,
              serverTaskId: typeof payload.generationTaskId === 'string' && payload.generationTaskId.trim()
                ? payload.generationTaskId.trim()
                : task.serverTaskId,
              outputScope: payload.outputScope ?? task.outputScope,
              error: payload.errorMessage || 'Generation failed.',
              failureCode: terminalImageGenerationFailureCode(payload),
              failureDetails: readImageGenerationFailureDetails(payload),
              recoveryPending: false,
            }),
          };
        }));
      }
    }

    videoSocketTerminalEventsByTaskIdRef.current.set(clientTaskId, payload);
  }, [findGenerationTaskRuntimeMetadata, kernel, setGenerationTasks]);

  // Recovery for the "generation charged but lost" bug. The terminal video result
  // arrives over a single Socket.IO emit to the user room; if the socket is absent
  // at that instant (ping timeout / tab stall / instance recycle / adapter lag),
  // Socket.IO never replays it and the waiter below hangs until its 60-min timeout
  // even though the server-side snapshot is already SUCCEEDED with finalAssetUrls.
  // This asks the server for the authoritative status of the tasks we are still
  // blocked on and feeds any terminal result back through the normal socket-event
  // handler, which resolves the waiter. Called on socket reconnect (Kernel
  // handleSocketReconnect) and once when a new socket waiter is registered.
  const reconcilePendingGenerationTasks = useCallback<KernelHandle['reconcilePendingGenerationTasks']>(async (taskIds, restoreList) => {
    if (restoreList?.signal?.aborted) return;
    const explicit = Array.isArray(taskIds)
      ? taskIds.map((id) => (typeof id === 'string' ? id.trim() : '')).filter((id) => id.length > 0)
      : [];
    const targetIds = (restoreList ? [] : explicit.length > 0
      ? Array.from(new Set(explicit))
      : Array.from(videoSocketTerminalWaitersByTaskIdRef.current.keys())).filter(id => !generationReconciliationInFlightRef.current.has(id));
    if (targetIds.length === 0 && !restoreList) return;
    const userId = kernel.current?.getUserContext().dbUserId;
    const restoreKey = restoreList ? `list:${userId}:${restoreList.projectId}:${restoreList.chatSessionId}` : null;
    const requestController = new AbortController();
    const requestSignal = restoreList?.signal ? AbortSignal.any([requestController.signal, restoreList.signal]) : requestController.signal;
    if (restoreKey && generationDiscoveryInFlightRef.current.get(restoreKey)?.aborted === false) return;
    if (restoreKey) generationDiscoveryInFlightRef.current.set(restoreKey, requestSignal);
    const restoreVersion = generationRestoreVersionRef.current;
    targetIds.forEach(id => generationReconciliationInFlightRef.current.add(id));
    const waitingAtRequest = new Map(targetIds.map(id => [id, videoSocketTerminalWaitersByTaskIdRef.current.get(id)]));
    const tasksAtRequest = new Map(targetIds.map(id => [id, findGenerationTaskRuntimeMetadata(id).task]));
    const requestTimeout = setTimeout(() => requestController.abort(), 10_000);

    try {
      const response = await fetch('/api/generation-tasks/reconcile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskIds: targetIds, ...(restoreList ? { restoreList: { projectId: restoreList.projectId, chatSessionId: restoreList.chatSessionId } } : {}) }),
        signal: requestSignal,
      });
      if (!response.ok) {
        console.warn(`[useGenerationTasks] Generation task reconciliation failed (${response.status}).`);
        return;
      }
      const data = (await response.json()) as { tasks?: unknown; missingTaskIds?: unknown; restoredTasks?: GenerationTask[]; completedLayerIds?: string[]; latestListGenerationTaskIds?: string[] };
      if (restoreList) {
        const current = kernel.current;
        if (!current || restoreList.signal?.aborted || generationRestoreVersionRef.current !== restoreVersion || current.getUserContext().dbUserId !== userId
          || current.getActiveChatSessionId() !== restoreList.chatSessionId
          || current.getLastProjectId(restoreList.chatSessionId) !== restoreList.projectId
          || !Array.isArray(data.restoredTasks)) return;
        const restoredTasks = data.restoredTasks.filter(task => task.projectId === restoreList.projectId
          && task.chatSessionId === restoreList.chatSessionId && task.listAttempt?.active === false);
        const stoppedAt = generationStoppedAtBySessionRef.current.get(restoreList.chatSessionId);
        for (const task of restoredTasks) {
          if (task.isAgentSpawned && stoppedAt !== undefined && task.listPresentation?.snapshotCreatedAt !== undefined
            && task.listPresentation.snapshotCreatedAt <= stoppedAt && !cancelledGenerationTaskIdsRef.current.has(task.id)) {
            cancelledGenerationTaskIdsRef.current.add(task.id);
            void finalizeGenerationTaskSnapshot(task.serverTaskId, task.id, [], {
              chatSessionId: task.chatSessionId, projectId: task.projectId, outputScope: task.outputScope,
              taskStatus: 'ABORTED', failureMessage: 'Cancelled by user stop request.',
            });
          }
        }
        setGenerationTasks(restoreList.projectId, previous => {
          const next = [...previous];
          let changed = false;
          for (const task of restoredTasks) {
            if (cancelledGenerationTaskIdsRef.current.has(task.id)
              || cancelledGenerationTaskIdsRef.current.has(task.listAttempt!.rootTaskId)) continue;
            const index = next.findIndex(existing => (existing.listAttempt?.rootTaskId ?? existing.id) === task.listAttempt!.rootTaskId);
            if (index < 0) { next.push(task); changed = true; }
            else {
              const existing = next[index]!;
              const incomingTime = task.listPresentation?.snapshotCreatedAt;
              const existingTime = existing.listPresentation?.snapshotCreatedAt ?? existing.startedAt;
              const newer = incomingTime !== undefined && existingTime !== undefined
                ? incomingTime > existingTime || (incomingTime === existingTime && existing.listPresentation?.snapshotCreatedAt !== undefined
                  && (task.serverTaskId ?? '').localeCompare(existing.serverTaskId ?? '') > 0)
                : task.listAttempt!.number > (existing.listAttempt?.number ?? 1);
              if (!existing.isOutcomeReported && !existing.listAttempt?.active
                && existing.id !== task.id && newer) { next[index] = task; changed = true; }
            }
          }
          return changed ? next : previous;
        });
        // Only initialize missing reload state; an explicit [] or a newer dispatch wins.
        if (current.readEntityRuntimeState('ChatSession', restoreList.chatSessionId).latestListGenerationTaskIds === undefined
          && Array.isArray(data.latestListGenerationTaskIds) && data.latestListGenerationTaskIds.length > 0 && stoppedAt === undefined) {
          current.patchEntityRuntimeState('ChatSession', restoreList.chatSessionId, {
            latestListGenerationTaskIds: data.latestListGenerationTaskIds.filter(id => typeof id === 'string' && id.length > 0
              && !cancelledGenerationTaskIdsRef.current.has(id)),
          });
        }
        // A result may finish after inventory loaded but before this first lookup.
        // Hydrate its existing layer; never create a new task or rerun the provider.
        if (Array.isArray(data.completedLayerIds) && data.completedLayerIds.length) {
          await current.ensureInventoryIds('StudioLayer', data.completedLayerIds.filter(id => typeof id === 'string' && id.length > 0));
        }
        return;
      }
      if (kernel.current?.getUserContext().dbUserId !== userId || !Array.isArray(data.tasks)) return;
      const payloads = Array.isArray(data.tasks) ? data.tasks : [];
      let resolvedCount = 0;
      payloads.forEach((payload) => {
        if (!isGenerationTaskStatusSocketPayload(payload)) return;
        if (!targetIds.includes(payload.clientTaskId) && !targetIds.includes(payload.generationTaskId ?? '')) return;
        const waiter = videoSocketTerminalWaitersByTaskIdRef.current.get(payload.clientTaskId);
        const { task } = findGenerationTaskRuntimeMetadata(payload.clientTaskId);
        if (tasksAtRequest.get(payload.clientTaskId) && !task) return;
        // Expiring an uncertain wait releases the command, not the paid job.
        // Actual Stop removes the task, so its late response still cannot revive it.
        if (waitingAtRequest.get(payload.clientTaskId) && waitingAtRequest.get(payload.clientTaskId) !== waiter && !task?.recoveryPending) return;
        // Feed through the same path the live socket event uses. It resolves an
        // active waiter, or caches the terminal event for a waiter about to be
        // registered; it is a no-op for tasks already reported/errored.
        handleGenerationTaskStatusSocketEvent(payload);
        if (payload.status !== 'RUNNING') resolvedCount += 1;
      });
      const missingIds = Array.isArray(data.missingTaskIds) ? data.missingTaskIds : [];
      targetIds.forEach(id => {
        const waiter = videoSocketTerminalWaitersByTaskIdRef.current.get(id);
        if (!waiter?.projectId || !missingIds.includes(id)) return;
        if (waitingAtRequest.get(id) !== waiter) return;
        waiter.missingSince ??= Date.now();
        waiter.missingCount += 1;
        if (waiter.missingCount >= 3 && Date.now() - waiter.missingSince >= 20_000) {
          const error = new Error('Generation submission not confirmed. Check status before trying again; the original request may still start.');
          error.name = 'GenerationSubmissionUnconfirmedError';
          waiter.reject(error);
        }
      });
      // "Check status" on an unconfirmed launch has no waiter left: when the server still holds no
      // record well past any server-side preparation deadline, the request never started and never
      // can, so the tile is released for Retry and dismissal instead of checking forever.
      targetIds.forEach(id => {
        if (!missingIds.includes(id) || videoSocketTerminalWaitersByTaskIdRef.current.has(id)) return;
        const { task, projectId } = findGenerationTaskRuntimeMetadata(id);
        if (!task?.recoveryPending || !projectId || Date.now() - (task.startedAt ?? Date.now()) < NEVER_STARTED_AFTER_MS) return;
        setGenerationTasks(projectId, previous => previous.map(item => item.id === id
          ? { ...item, recoveryPending: false, error: GENERATION_NEVER_STARTED_MESSAGE } : item));
      });
      if (resolvedCount > 0) {
        console.log(`[useGenerationTasks] Recovered ${resolvedCount} pending generation task(s) from server snapshots.`);
      }
    } catch (error) {
      if (!restoreList?.signal?.aborted) console.warn('[useGenerationTasks] Generation task reconciliation error.', error);
    } finally {
      clearTimeout(requestTimeout);
      targetIds.forEach(id => generationReconciliationInFlightRef.current.delete(id));
      if (restoreKey && generationDiscoveryInFlightRef.current.get(restoreKey) === requestSignal) generationDiscoveryInFlightRef.current.delete(restoreKey);
    }
  }, [handleGenerationTaskStatusSocketEvent, findGenerationTaskRuntimeMetadata, setGenerationTasks, kernel]);

  const dismissFailedGenerationTask = useCallback<KernelHandle['dismissFailedGenerationTask']>(async (projectId, taskId) => {
    const userId = kernel.current?.getUserContext().dbUserId;
    const task = getGenerationTasks(projectId).find(item => item.id === taskId);
    if (!task?.error || task.recoveryPending || isListTaskRecovering(task)) return;
    if (task.listAttempt) {
      const response = await fetch(`/api/generation-tasks/${encodeURIComponent(task.serverTaskId ?? task.id)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dismissFailed: true }),
        signal: AbortSignal.timeout(10_000),
      });
      if (!response.ok) throw new Error('Could not dismiss this failed generation. Try again.');
    }
    if (kernel.current?.getUserContext().dbUserId !== userId) return;
    // Retain a runtime tombstone so an in-flight hydration response cannot revive the card. Matched by
    // id: a reconcile during the request above replaces the task object, and the card stayed.
    setGenerationTasks(projectId, previous => previous.map(item => item.id === taskId ? { ...item, isOutcomeReported: true } : item));
  }, [getGenerationTasks, setGenerationTasks, kernel]);

  const waitForVideoSocketTerminal = useCallback((
    task: GenerationTask,
    originalError: unknown,
    submissionStartedAt: number,
    abortSignal?: AbortSignal,
    force = false,
  ): Promise<GenerationTaskStatusSocketPayload | null> => {
    const elapsedMs = Date.now() - submissionStartedAt;
    if (!force && !isLateVideoSocketBackedSubmissionError(task, originalError, elapsedMs)) {
      return Promise.resolve(null);
    }

    const taskId = typeof task.id === 'string' ? task.id.trim() : '';
    if (!taskId) return Promise.resolve(null);

    const cachedTerminal = videoSocketTerminalEventsByTaskIdRef.current.get(taskId);
    if (cachedTerminal) {
      videoSocketTerminalEventsByTaskIdRef.current.delete(taskId);
      return Promise.resolve(cachedTerminal);
    }

    const terminalPromise = new Promise<GenerationTaskStatusSocketPayload>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let recoveryInterval: ReturnType<typeof setInterval> | null = null;
      let handleAbort: () => void = () => {};
      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (recoveryInterval) clearInterval(recoveryInterval);
        abortSignal?.removeEventListener('abort', handleAbort);
        videoSocketTerminalWaitersByTaskIdRef.current.delete(taskId);
      };
      handleAbort = () => {
        cleanup();
        reject(createTaskCancellationError(taskId, 'cancelled while waiting for video socket terminal event.'));
      };

      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Timed out waiting for video generation terminal socket event after HTTP gateway timeout.'));
      }, VIDEO_SOCKET_TERMINAL_TIMEOUT_MS);

      if (abortSignal) {
        if (abortSignal.aborted) {
          handleAbort();
          return;
        }
        abortSignal.addEventListener('abort', handleAbort, { once: true });
      }

      videoSocketTerminalWaitersByTaskIdRef.current.set(taskId, {
        projectId: typeof task.projectId === 'string' ? task.projectId.trim() || null : null,
        lastConfirmedAt: Date.now(), missingSince: null, missingCount: 0,
        resolve: (payload) => {
          cleanup();
          resolve(payload);
        },
        reject: (error) => {
          cleanup();
          reject(error);
        },
      });
      if (task.outputScope?.kind === 'PROJECT') recoveryInterval = setInterval(() => {
        const waiter = videoSocketTerminalWaitersByTaskIdRef.current.get(taskId);
        if (waiter && Date.now() - waiter.lastConfirmedAt >= 90_000) {
          const error = new Error('Generation status unavailable. The connection was interrupted. Check status before trying again; the original generation may still finish.');
          error.name = 'GenerationRecoveryUnavailableError';
          waiter.reject(error);
          return;
        }
        void reconcilePendingGenerationTasks([taskId]);
      }, 10_000);
    });

    // The terminal socket event may have already fired (and been lost) during a
    // disconnect that healed before this waiter existed, the live handler had no
    // waiter to resolve and nothing to cache. Ask the server now; the executor
    // above runs synchronously, so the waiter is already registered and a terminal
    // snapshot resolves it immediately instead of waiting the full 60 minutes.
    void reconcilePendingGenerationTasks([taskId]);

    return terminalPromise;
  }, [reconcilePendingGenerationTasks]);

  const cancelGenerationTasksForSession = useCallback<KernelHandle['cancelGenerationTasksForSession']>((sessionId) => {
    const k = kernel.current;
    if (!k) return;
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!normalizedSessionId) return;
    generationRestoreVersionRef.current += 1;
    generationStoppedAtBySessionRef.current.set(normalizedSessionId, Date.now());
    k.setIsSubmittingAITask(false);

    const projectIds = new Set<string>();
    Object.keys(k.getInventory().Project.registry).forEach((projectId) => {
      if (projectId.trim().length > 0) projectIds.add(projectId.trim());
    });
    const sessionProjectId = k.getLastProjectId?.(normalizedSessionId);
    if (typeof sessionProjectId === 'string' && sessionProjectId.trim().length > 0) {
      projectIds.add(sessionProjectId.trim());
    }
    const activeSessionId = k.getActiveChatSessionId?.();
    if (activeSessionId === normalizedSessionId) {
      const activeProjectId = k.resolveActiveSessionProjectId?.();
      if (typeof activeProjectId === 'string' && activeProjectId.trim().length > 0) {
        projectIds.add(activeProjectId.trim());
      }
    }

    const cancelledTasks: GenerationTask[] = [];
    projectIds.forEach((projectId) => {
      setGenerationTasks(projectId, (previousTasks) => {
        const nextTasks: GenerationTask[] = [];
        previousTasks.forEach((task) => {
          const taskSessionId =
            typeof task.chatSessionId === 'string' && task.chatSessionId.trim().length > 0
              ? task.chatSessionId.trim()
              : null;
          // chatSessionId ne dit que « affiche-moi dans ce fil » : une generation
          // lancee a la main dans le Studio le porte aussi, des qu'une session pointe
          // sur le projet. Seul isAgentSpawned marque ce que le tour agentique a lance,
          // et c'est la seule chose que son arret doit emporter. Une tache deja livree reste
          // dans la liste : l'annuler reecrirait en ABORTED un rendu MERGED.
          if (taskSessionId === normalizedSessionId && task.isAgentSpawned === true && isGenerationTaskInFlight(task)) {
            cancelledTasks.push(task);
          } else {
            nextTasks.push(task);
          }
        });
        return nextTasks;
      });
    });

    const cancelledTaskIds = new Set(cancelledTasks.map((task) => task.id));
    cancelledTasks.forEach(task => {
      cancelledGenerationTaskIdsRef.current.add(task.id);
      cancelledGenerationTaskIdsRef.current.add(task.listAttempt?.rootTaskId ?? task.id);
    });
    videoSocketTerminalWaitersByTaskIdRef.current.forEach((waiter, taskId) => {
      if (!cancelledTaskIds.has(taskId)) return;
      waiter.reject(createTaskCancellationError(taskId, 'explicitly cancelled by stop request.'));
      videoSocketTerminalWaitersByTaskIdRef.current.delete(taskId);
      videoSocketTerminalEventsByTaskIdRef.current.delete(taskId);
    });
    pendingTaskPromisesRef.current.forEach((promiseCallbacks, taskId) => {
      // Auparavant un waiter etait rejete des que sa session correspondait, ce qui
      // rejetterait la promesse d'une generation manuelle que la boucle ci-dessus
      // vient justement d'epargner. On ne rejette que ce qui a reellement ete annule.
      if (!cancelledTaskIds.has(taskId)) return;
      const cancellationError = createTaskCancellationError(taskId, 'explicitly cancelled by stop request.');
      promiseCallbacks.reject(cancellationError);
      pendingTaskPromisesRef.current.delete(taskId);
      generationSpawnTimelineEventIdByTaskIdRef.current.delete(taskId);
    });

    cancelledTasks.forEach((task) => {
      if (!task.serverTaskId) return;
      void finalizeGenerationTaskSnapshot(task.serverTaskId, task.id, [], {
        metadata: {
          workspaceId: task.workspaceId ?? null,
          chatSessionId: normalizedSessionId,
          projectId: task.projectId ?? null,
          taskType: task.type,
          model: task.model,
        },
        chatSessionId: normalizedSessionId,
        outputScope: task.outputScope,
        workspaceId: task.workspaceId ?? null,
        projectId: task.projectId ?? null,
        taskStatus: 'ABORTED',
        failureMessage: 'Cancelled by user stop request.',
      });
    });

    if (cancelledTasks.length > 0) {
      console.log(`[Kernel] Cancelled ${cancelledTasks.length} generation task(s) for session ${normalizedSessionId}.`);
    }
  }, [kernel, setGenerationTasks]);

  const emitGenerationTaskSpawnTimelineEvent = useCallback((task: GenerationTask, serverTaskId: string | null) => {
    const k = kernel.current;
    if (!k) return;
    const inventory = k.getInventory();
    const projectId =
      typeof task.projectId === 'string' && task.projectId.trim().length > 0
        ? task.projectId.trim()
        : null;
    const activeSessionId = k.getActiveChatSessionId();
    const activeSessionProjectIdRaw = activeSessionId ? k.getLastProjectId(activeSessionId) : null;
    const activeSessionProjectId =
      typeof activeSessionProjectIdRaw === 'string' ? activeSessionProjectIdRaw.trim() : '';
    const chatSessionId =
      typeof task.chatSessionId === 'string' && task.chatSessionId.trim().length > 0
        ? task.chatSessionId.trim()
        : activeSessionId && projectId && activeSessionProjectId === projectId
          ? activeSessionId
          : null;
    if (!chatSessionId || !projectId) return;
    // THE LIST SURFACE NEVER GETS THIS LINE AT ALL, loop or no loop. The batch canvas next to the
    // chat already shows every output and its state, so "<Model> launched : <layer>" is the machine
    // narrating itself to a marketing manager - twelve of them for one batch. The owner asked for
    // them gone on 2026-08-25. Not written rather than hidden at render: an event nobody reads is
    // a row in the database and a line in every future projector that forgets to filter it.
    if (k.getState?.()?.studioViewMode === 'marketing') return;

    const prompt =
      task.params && typeof (task.params as { prompt?: unknown }).prompt === 'string'
        ? ((task.params as { prompt?: string }).prompt ?? '').trim()
        : '';
    const workspaceId =
      typeof task.workspaceId === 'string' && task.workspaceId.trim().length > 0
        ? task.workspaceId.trim()
        : null;
    const workspace = workspaceId ? inventory.StudioWorkspace.registry[workspaceId] : null;
    const workspaceName =
      typeof workspace?.name === 'string' && workspace.name.trim().length > 0
        ? workspace.name.trim()
        : null;
    const targetVersionId =
      typeof task.parentVersionId === 'string' && task.parentVersionId.trim().length > 0
        ? task.parentVersionId.trim()
        : typeof workspace?.activeVersionId === 'string' && workspace.activeVersionId.trim().length > 0
          ? workspace.activeVersionId.trim()
          : null;
    const targetVersion = targetVersionId
      ? inventory.StudioWorkspaceVersion.registry[targetVersionId]
      : null;
    const targetVersionName =
      typeof targetVersion?.name === 'string' && targetVersion.name.trim().length > 0
        ? targetVersion.name.trim()
        : null;

    const payload: Record<string, unknown> = {
      taskId: task.id,
      snapshotId: serverTaskId,
      projectId,
      workspaceId,
      workspaceName,
      layerId: task.layer?.id ?? null,
      taskType: task.type,
      model: task.model,
      mergeStrategy: task.mergeStrategy ?? null,
      layerConsolidation: task.layerConsolidation ?? null,
      parentVersionId: task.parentVersionId ?? null,
      targetVersionId,
      targetVersionName,
      prompt: prompt.length > 0 ? prompt : null,
      createdAt: new Date().toISOString(),
    };

    const taskId = normalizeTimelineString(task.id);
    void (async () => {
      try {
        const result = await k.mutateInventory([{
          action: 'CREATE',
          entity: 'TimelineEvent',
          tempId: `timeline-event-generation-spawn-${uuidv4()}`,
          payload: {
            chatSessionId,
            targetEntityType: 'Project',
            targetEntityId: projectId,
            category: 'MUTATION',
            eventType: 'STUDIO_GENERATION_TASK_SPAWNED',
            payload,
          },
        }] as any, `Create generation spawn timeline event for session ${chatSessionId}`);

        const createdEventId = normalizeTimelineString(result?.[0]?.id);
        if (taskId && createdEventId) {
          generationSpawnTimelineEventIdByTaskIdRef.current.set(taskId, createdEventId);
        }
      } catch (error) {
        console.error('[useGenerationTasks] Failed to emit generation spawn timeline event', {
          taskId: task.id,
          chatSessionId,
          error,
        });
      }
    })();
  }, [kernel]);

  const findGenerationSpawnTimelineEventId = useCallback((task: GenerationTask): string | null => {
    const k = kernel.current;
    if (!k) return null;

    const taskId = normalizeTimelineString(task.id);
    if (!taskId) return null;

    const cached = generationSpawnTimelineEventIdByTaskIdRef.current.get(taskId);
    if (cached) return cached;

    const projectId = normalizeTimelineString(task.projectId);
    if (!projectId) return null;

    const subjectKey = buildProjectTimelineSubjectKey(projectId);
    const timelineRegistry = k.getInventory().TimelineEvent.registry;
    const indexedTimelineIds = k.getState().timelineFeedIndex.bySubjectKey[subjectKey] ?? [];
    const resolvedEventId =
      indexedTimelineIds.find((eventId) => {
        const event = timelineRegistry[eventId];
        if (!event || event.eventType !== 'STUDIO_GENERATION_TASK_SPAWNED') return false;
        const payload = event.payload as Record<string, unknown> | null;
        const payloadTaskId = normalizeTimelineString(payload?.taskId);
        return payloadTaskId === taskId;
      }) ?? null;

    if (resolvedEventId) {
      generationSpawnTimelineEventIdByTaskIdRef.current.set(taskId, resolvedEventId);
      return resolvedEventId;
    }

    return null;
  }, [kernel]);

  const updateGenerationTaskSpawnTimelineEvent = useCallback((
    task: GenerationTask,
    patch: Record<string, unknown>
  ) => {
    const k = kernel.current;
    if (!k) return;

    const targetEventId = findGenerationSpawnTimelineEventId(task);
    if (!targetEventId) return;

    const timelineEvent = k.getInventory().TimelineEvent.registry[targetEventId];
    const currentPayload =
      timelineEvent?.payload && typeof timelineEvent.payload === 'object' && !Array.isArray(timelineEvent.payload)
        ? (timelineEvent.payload as Record<string, unknown>)
        : {};
    const nextPayload = { ...currentPayload, ...patch };
    void k.mutateInventory([{
      action: 'UPDATE',
      entity: 'TimelineEvent',
      id: targetEventId,
      payload: {
        payload: nextPayload,
      },
    }] as any, `Update generation spawn timeline event for task ${task.id}`).catch((error) => {
      console.error('[useGenerationTasks] Failed to update generation spawn timeline event', {
        taskId: task.id,
        eventId: targetEventId,
        error,
      });
    });
  }, [findGenerationSpawnTimelineEventId, kernel]);

  const reportTaskSuccess = useCallback<KernelHandle['reportTaskSuccess']>((taskId, resultData) => {
    const promiseCallbacks = pendingTaskPromisesRef.current.get(taskId);
    if (promiseCallbacks) {
      promiseCallbacks.resolve({ id: taskId, status: 'SUCCESS', data: compactTaskCompletionData(resultData) });
      pendingTaskPromisesRef.current.delete(taskId);
    }
    generationSpawnTimelineEventIdByTaskIdRef.current.delete(taskId);
  }, []);

  const reportTaskFailure = useCallback<KernelHandle['reportTaskFailure']>((taskId, error) => {
    const promiseCallbacks = pendingTaskPromisesRef.current.get(taskId);
    if (promiseCallbacks) {
      promiseCallbacks.resolve({ id: taskId, status: 'FAILURE', error });
      pendingTaskPromisesRef.current.delete(taskId);
    }
    generationSpawnTimelineEventIdByTaskIdRef.current.delete(taskId);
  }, []);

  const onTaskOutcome = useCallback<KernelHandle['onTaskOutcome']>((taskId, status, data, error) => {
    const promiseCallbacks = pendingTaskPromisesRef.current.get(taskId);
    if (!promiseCallbacks) return;
    if (status === 'SUCCESS') {
      console.log(`[Kernel] Task ${taskId} SUCCESS reported, resolving promise.`);
      promiseCallbacks.resolve({ id: taskId, status: 'SUCCESS', data: compactTaskCompletionData(data) });
    } else {
      console.log(`[Kernel] Task ${taskId} FAILURE reported, resolving promise with failure status.`);
      promiseCallbacks.resolve({ id: taskId, status: 'FAILURE', error });
    }
    pendingTaskPromisesRef.current.delete(taskId);
    generationSpawnTimelineEventIdByTaskIdRef.current.delete(taskId);
  }, []);

  const waitForTask = useCallback<KernelHandle['waitForTask']>((taskId): Promise<TaskCompletionData> => {
    const { task: existingTask, projectId, chatSessionId } = findGenerationTaskRuntimeMetadata(taskId);
    if (existingTask?.error) {
      return Promise.resolve({
        id: taskId,
        status: 'FAILURE',
        error: Object.assign(new Error(existingTask.error), { code: existingTask.failureCode, failureDetails: readImageGenerationFailureDetails(existingTask), generationFailureReported: Boolean(existingTask.failureCode) }),
      });
    }
    if (existingTask?.isOutcomeReported) {
      return Promise.resolve({
        id: taskId,
        status: 'SUCCESS',
        data: { layer: compactTaskCompletionLayer(existingTask.layer), urls: existingTask.urls },
      });
    }
    return new Promise<TaskCompletionData>((resolve, reject) => {
      if (pendingTaskPromisesRef.current.has(taskId)) {
        return reject(new Error(`[Kernel] Already waiting for task ID ${taskId}.`));
      }
      pendingTaskPromisesRef.current.set(taskId, { resolve, reject, chatSessionId, projectId });
    });
  }, [findGenerationTaskRuntimeMetadata]);

  const enqueueLayerPersistence = useCallback((layerId: string, runner: () => Promise<void>): Promise<void> => {
    const normalizedLayerId = typeof layerId === 'string' ? layerId.trim() : '';
    if (!normalizedLayerId) return Promise.resolve();

    const previous = layerPersistenceQueueRef.current.get(normalizedLayerId) ?? Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(async () => {
        await runner();
      });

    layerPersistenceQueueRef.current.set(normalizedLayerId, next);
    // The failure is reported through `next`, which the caller holds.
    void next.finally(() => {
      const current = layerPersistenceQueueRef.current.get(normalizedLayerId);
      if (current === next) {
        layerPersistenceQueueRef.current.delete(normalizedLayerId);
      }
    }).catch(() => undefined);
    return next;
  }, []);

  const persistGeneratedLayerAttach = useCallback((
    layer: Layer,
    projectId: string,
    context: string,
    options?: { throwOnError?: boolean },
  ): Promise<void> => {
    const workspaceId = typeof layer.layerWorkspaceId === 'string' && layer.layerWorkspaceId.trim().length > 0
      ? layer.layerWorkspaceId.trim()
      : null;
    if (!projectId.trim()) {
      console.error(`[${context}] Cannot persist generated layer "${layer.id}": missing projectId.`);
      return Promise.resolve();
    }

    return enqueueLayerPersistence(layer.id, async () => {
      const k = kernel.current;
      if (!k) return;
      // The whole output goes on the layer's own save, the one writer of its row, whose write carries its uploaded pixels.
      // A failed upload does not fail the output the provider delivered: the save told the user and keeps it for the next write.
      const saved = await saveLayerNow(k, layer.id, buildStudioLayerMutationPayload(layer, projectId, workspaceId));
      const state = k.getAutosaveState('StudioLayer', layer.id, STUDIO_LAYER_DTO_AUTOSAVE_SCOPE);
      if (!saved && state.saveState === 'error' && state.lastError !== LAYER_PIXELS_UPLOAD_FAILED) {
        throw new Error(`Generated layer "${layer.id}" could not be saved.`);
      }
    }).catch((error) => {
      console.error(`[${context}] Failed to persist generated layer "${layer.id}".`, error);
      if (options?.throwOnError) throw error;
    });
  }, [enqueueLayerPersistence, kernel]);

  const persistGeneratedLayerDetach = useCallback((layerId: string, context: string) => {
    enqueueLayerPersistence(layerId, async () => {
      const k = kernel.current;
      // Only a row is detached: a layer without one was never created, or is back since (a redo).
      if (!k?.getState().inventory.StudioLayer.registry[layerId]) return;
      if (!(await saveLayerNow(k, layerId, { workspaceId: null })) && k.getAutosaveState('StudioLayer', layerId, STUDIO_LAYER_DTO_AUTOSAVE_SCOPE).saveState === 'error') {
        throw new Error(`Generated layer "${layerId}" could not be detached.`);
      }
    }).catch((error) => {
      console.error(`[${context}] Failed to detach generated layer "${layerId}".`, error);
    });
  }, [enqueueLayerPersistence, kernel]);

  const nameListOutputs = useCallback(async (projectId: string, layerIds: string[], signal?: AbortSignal) => {
    const k = kernel.current;
    if (!k) return;
    const userId = k.getUserContext().dbUserId;
    const current = () => !signal?.aborted && kernel.current?.getUserContext().dbUserId === userId;
    const ids = [...new Set(layerIds)].slice(0, 100);
    for (let offset = 0; offset < ids.length && current(); offset += 2) {
      await Promise.all(ids.slice(offset, offset + 2).map(async id => {
        const layer = k.getLayerRegistry()[id];
        if (!layer || layer.projectId !== projectId || layer.source !== 'generated'
          || !isGenericListOutputName(layer.name, layer.params && 'model' in layer.params ? layer.params.model : null)) return;
        try {
          const response = await fetch('/api/ai/suggest-layer-name', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, signal,
            body: JSON.stringify({ automaticListOutput: true, projectId, layerId: id }),
          });
          if (!response.ok) throw new Error('Automatic output name is unavailable.');
          if (current()) await k.syncEntity('StudioLayer', id, { force: true, requireFresh: true });
        } catch (error) {
          if (current()) console.warn('Output remains available without an automatic name.', error);
        }
      }));
    }
  }, [kernel]);

  const fetchAndApplyLayerName = useCallback(async (layer: Layer, task: GenerationTask, abortSignal?: AbortSignal) => {
    if (!layer.imageData && !(layer.type === 'video' && layer.videoFramesUrls && layer.videoFramesUrls.length > 0)) {
        console.warn('Cannot suggest name: Layer has no ImageData or video frames.', layer.id);
        return;
    }

    const k = kernel.current;
    if (!k) return;
    const inventory = k.getInventory();
    const scopedLayerIds = new Set<string>();
    const normalizedProjectId = typeof task.projectId === 'string' ? task.projectId.trim() : '';
    const isCancelled = () => {
      if (abortSignal?.aborted) return true;
      const taskSessionId =
        typeof task.chatSessionId === 'string' && task.chatSessionId.trim().length > 0
          ? task.chatSessionId.trim()
          : null;
      if (taskSessionId && task.isAgentSpawned === true
        && k.readEntityRuntimeState('ChatSession', taskSessionId).isStopRequested === true) {
        return true;
      }
      if (normalizedProjectId && !getGenerationTasks(normalizedProjectId).some((candidate) => candidate.id === task.id)) {
        return true;
      }
      return false;
    };
    if (isCancelled()) return;
    if (normalizedProjectId) {
      const project = inventory.Project.registry[normalizedProjectId];
      const workspaceIds = Array.isArray(project?.workspaceIds)
        ? project.workspaceIds.filter((workspaceId): workspaceId is string => typeof workspaceId === 'string' && workspaceId.trim().length > 0)
        : [];
      workspaceIds.forEach((workspaceId) => {
        const workspace = inventory.StudioWorkspace.registry[workspaceId];
        (workspace?.layerIds ?? []).forEach((layerId) => {
          if (typeof layerId === 'string' && layerId.trim().length > 0) {
            scopedLayerIds.add(layerId);
          }
        });
      });
    } else if (typeof task.workspaceId === 'string' && task.workspaceId.trim().length > 0) {
      const workspace = inventory.StudioWorkspace.registry[task.workspaceId];
      (workspace?.layerIds ?? []).forEach((layerId) => {
        if (typeof layerId === 'string' && layerId.trim().length > 0) {
          scopedLayerIds.add(layerId);
        }
      });
    }
    const layerRegistry = k.getLayerRegistry();
    const layers = [...scopedLayerIds]
      .map((layerId) => layerRegistry[layerId])
      .filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate));
    const existingNames = layers.map(l => l.name).filter(name => !!name);
    const MAX_FRAMES_FOR_NAMING = 10;
    const namingScope: 'project' | 'workspace' = normalizedProjectId ? 'project' : 'workspace';

    const orgDetails = kernel.current?.getCurrentOrganisation();
    const preferredLanguage = orgDetails?.favoriteLanguage === 'FRENCH' ? 'French' : 'English';
    const params = task.params as BaseParams | undefined;
    const effectivePrompt = params?.noParamsSave ? undefined : params?.prompt;

    try {
        let apiBody: any;

        if (layer.type === 'video' && layer.videoFramesUrls && layer.videoFramesUrls.length > 0) {
            let framesToProcess = layer.videoFramesUrls;
            if (framesToProcess.length > MAX_FRAMES_FOR_NAMING) {
                const step = Math.ceil(framesToProcess.length / MAX_FRAMES_FOR_NAMING);
                const downsampledFrames: string[] = [];
                for (let i = 0; i < framesToProcess.length - 1; i += step) {
                    let frame = framesToProcess[i];
                    if(frame !== undefined) downsampledFrames.push(frame);
                }
                let lastFrame = framesToProcess[framesToProcess.length - 1];
                if(lastFrame !== undefined) downsampledFrames.push(lastFrame);
                framesToProcess = downsampledFrames;
            }

            const frameBlobs = await Promise.all(
                framesToProcess.map(url => fetch(url, { signal: abortSignal }).then(res => res.blob()))
            );
            if (isCancelled()) return;

            const b64Frames = await Promise.all(
                frameBlobs.map(blob => new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        if (typeof reader.result === 'string') {
                            resolve(reader.result.split(',')[1] || '');
                        } else {
                            reject(new Error('FileReader did not return a string for Base64 conversion'));
                        }
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                }))
            );
            if (isCancelled()) return;

            apiBody = {
                imagesDataB64: b64Frames,
                imageMimeType: 'image/webp', // Frames are webp
                existingLayerNames: existingNames,
                preferredLanguage: preferredLanguage,
                taskPrompt: effectivePrompt,
                mainImageUrl: task.mainImagePublicUrl,
            };
        } else if (layer.imageData) {
            const b64ImageData = await imageDataToBase64(layer.imageData, 'image/jpeg', 1024);
            if (isCancelled()) return;
            apiBody = {
                imageDataB64: b64ImageData,
                imageMimeType: 'image/jpeg',
                existingLayerNames: existingNames,
                preferredLanguage: preferredLanguage,
                taskPrompt: effectivePrompt,
                mainImageUrl: task.mainImagePublicUrl,
            };
        } else {
             console.warn('Cannot suggest name: Layer has no ImageData or video frames.', layer.id);
             return;
        }

        apiBody.layerId = layer.id;
        apiBody.namingScope = namingScope;

        const response = await fetch('/api/ai/suggest-layer-name', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: abortSignal,
            body: JSON.stringify(apiBody),
        });
        if (isCancelled()) return;

        if (!response.ok) {
            return;
        }

        const result = await response.json();
        if (isCancelled()) return;
        const suggestedName = result.suggestedName;

        if (suggestedName && typeof suggestedName === 'string') {
            console.log(`Suggested name for layer ${layer.id}: ${suggestedName}`);

            if (normalizedProjectId) {
              setGenerationTasks(normalizedProjectId, prevTasks =>
                  prevTasks.map(task =>
                      task.layer && task.layer.id === layer.id
                          ? task.isOutcomeReported
                            ? compactCompletedGenerationTask({ ...task, layer: { ...task.layer, name: suggestedName } })
                            : { ...task, layer: { ...task.layer, name: suggestedName } }
                          : task
                  )
              );
            }
        } else {
            console.warn('No valid name suggested or empty name received.');
        }
    } catch {
        // Best effort only: silently ignore naming failures.
    }
  }, [kernel, setGenerationTasks, getGenerationTasks]);

  const runGenerationPostprocess = useCallback(async (
    target: ImageData,
    job: Parameters<typeof computeGenerationPostprocess>[1],
  ): Promise<ImageData> => {
    const start = performance.now();
    const { result, executionPath } = await runGenerationPostprocessWithFallback(
      kernel.current?.generationPostprocessWorkerRef ?? { current: null },
      target,
      job,
    );
    if (kernel.current?.getUserPreferences()?.performanceHudEnabled === true) {
      console.log('[Perf][generationPostprocess] done', {
        kind: job.kind,
        size: `${target.width}x${target.height}`,
        executionPath,
        totalMs: Math.round(performance.now() - start),
      });
    }
    return result;
  }, [kernel]);

  const processRepaintOnMask = useCallback(async (replicateImageUrl: string, task: GenerationTask, abortSignal?: AbortSignal): Promise<string[]> => {
    let uploadedMediaUrls: string[] = [];
    try {
        console.log("processRepaintOnMask: Starting processing", { replicateImageUrl, task});
        const replicateImageResponse = await retry(() => fetch(replicateImageUrl, { signal: abortSignal }), { retries: 5, delay: 5000, backoff: true, signal: abortSignal });
        if (!replicateImageResponse.ok) {
            console.error(`processRepaintOnMask: Failed to fetch replicate image: ${replicateImageUrl}, status: ${replicateImageResponse.status}, statusText: ${replicateImageResponse.statusText}`);
            return [];
        }

        let replicateImageBlob: Blob | null = null;
        try {
            replicateImageBlob = await replicateImageResponse.blob();
            if (!replicateImageBlob) {
                console.error("processRepaintOnMask: replicateImageBlob is null after fetch.");
                return [];
            }
        } catch (blobError) {
            console.error("processRepaintOnMask: Error creating replicateImageBlob:", blobError);
            return [];
        }
        const replicateImageURL = URL.createObjectURL(replicateImageBlob);
        if (!replicateImageURL) {
            console.error("processRepaintOnMask: replicateImageURL is null after createObjectURL.");
            return [];
        }


        const maskImageFile = task.maskFile;
        const maskImageURL = maskImageFile ? URL.createObjectURL(maskImageFile) : null;
        if (maskImageFile && !maskImageURL) {
            console.error("processRepaintOnMask: maskImageURL is null after createObjectURL for maskFile.");
            URL.revokeObjectURL(replicateImageURL);
            return [];
        }


        const imgReplicate = new Image();
        const imgMask = new Image();

        const imagePromises: Promise<any>[] = [
            new Promise((resolve, reject) => {
                imgReplicate.onload = resolve;
                imgReplicate.onerror = (error) => {
                    console.error("processRepaintOnMask: Error loading replicate image:", error);
                    reject("Error loading replicate image");
                };
                imgReplicate.src = replicateImageURL;
            }),
        ];
        if (maskImageURL) {
            imagePromises.push(new Promise((resolve, reject) => {
                imgMask.onload = resolve;
                imgMask.onerror = (error) => {
                    console.error("processRepaintOnMask: Error loading mask image:", error);
                    reject("Error loading mask image");
                };
                imgMask.src = maskImageURL;
            }));
        }

        try {
            await Promise.all(imagePromises);
        } catch (imageLoadError) {
            console.error("processRepaintOnMask: Promise.all for image loading rejected:", imageLoadError);
            URL.revokeObjectURL(replicateImageURL);
            if(maskImageURL) URL.revokeObjectURL(maskImageURL);
            return [];
        }


        const resizeCanvas = document.createElement('canvas');
        resizeCanvas.width = task.targetWidth;
        resizeCanvas.height = task.targetHeight;
        const resizeCtx = resizeCanvas.getContext('2d');
        if (!resizeCtx) {
            console.error("processRepaintOnMask: Resize canvas context not available.");
            URL.revokeObjectURL(replicateImageURL);
            if(maskImageURL) URL.revokeObjectURL(maskImageURL);
            return [];
        }

        resizeCtx.drawImage(imgReplicate, 0, 0, task.targetWidth, task.targetHeight);
        imgReplicate.src = resizeCanvas.toDataURL();
        await new Promise((resolve, reject) => {
            imgReplicate.onload = resolve;
            imgReplicate.onerror = (error) => {
                console.error("processRepaintOnMask: Error setting resized data URL to replicate image:", error);
                reject("Error setting resized data URL to replicate image");
            };
        });


        const maskCanvasForBounds = document.createElement('canvas');
        maskCanvasForBounds.width = task.targetWidth;
        maskCanvasForBounds.height = task.targetHeight;
        const maskCtxForBounds = maskCanvasForBounds.getContext('2d');
        if (!maskCtxForBounds) {
            console.error("processRepaintOnMask: Mask bounds canvas context not available.");
            URL.revokeObjectURL(replicateImageURL);
            if(maskImageURL) URL.revokeObjectURL(maskImageURL);
            return [];
        }


        let minX = task.targetWidth;
        let minY = task.targetHeight;
        let maxX = 0;
        let maxY = 0;
        let hasMaskedPixels = false;
        let maskDataForPixelCopy: ImageData | null = null;

        if (maskImageFile && maskCtxForBounds && imgMask.complete) {
            maskCanvasForBounds.width = imgMask.width;
            maskCanvasForBounds.height = imgMask.height;
            maskCtxForBounds.drawImage(imgMask, 0, 0);

            try {
                maskDataForPixelCopy = maskCtxForBounds.getImageData(0, 0, maskCanvasForBounds.width, maskCanvasForBounds.height);
                if (!maskDataForPixelCopy) {
                    console.error("processRepaintOnMask: maskDataForPixelCopy is null after getImageData.");
                    URL.revokeObjectURL(replicateImageURL);
                    if(maskImageURL) URL.revokeObjectURL(maskImageURL);
                    return [];
                }
            } catch (imageDataError) {
                console.error("processRepaintOnMask: Error getting mask image data:", imageDataError);
                URL.revokeObjectURL(replicateImageURL);
                if(maskImageURL) URL.revokeObjectURL(maskImageURL);
                return [];
            }


            for (let y = 0; y < task.targetHeight; y++) {
                for (let x = 0; x < task.targetWidth; x++) {
                    if (x < maskCanvasForBounds.width && y < maskCanvasForBounds.height) {
                        const pixelIndex = (y * maskCanvasForBounds.width + x) * 4;
                        if ((maskDataForPixelCopy.data[pixelIndex + 3] || 0) > 0) {
                            hasMaskedPixels = true;
                            minX = Math.min(minX, x);
                            minY = Math.min(minY, y);
                            maxX = Math.max(maxX, x);
                            maxY = Math.max(maxY, y);
                        }
                    }
                }
            }
        } else {
            hasMaskedPixels = true;
            minX = 0;
            minY = 0;
            maxX = task.targetWidth;
            maxY = task.targetHeight;
            maskDataForPixelCopy = maskCtxForBounds!.createImageData(task.targetWidth, task.targetHeight);
            if (!maskDataForPixelCopy) {
                console.error("processRepaintOnMask: maskDataForPixelCopy is null after createImageData (no mask).");
                URL.revokeObjectURL(replicateImageURL);
                if(maskImageURL) URL.revokeObjectURL(maskImageURL);
                return [];
            }
            for (let i = 0; i < maskDataForPixelCopy.data.length; i += 4) {
                maskDataForPixelCopy.data[i + 3] = 255;
            }
        }

        const boundingBoxWidth = Math.max(1, Math.ceil(maxX - minX));
        const boundingBoxHeight = Math.max(1, Math.ceil(maxY - minY));

        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = boundingBoxWidth;
        outputCanvas.height = boundingBoxHeight;
        const outputCtx = outputCanvas.getContext('2d');
        if (!outputCtx) {
            console.error("processRepaintOnMask: Output canvas context not available.");
            URL.revokeObjectURL(replicateImageURL);
            if(maskImageURL) URL.revokeObjectURL(maskImageURL);
            return [];
        }


        let outputImageData: ImageData | null = null;

        if (outputCtx && hasMaskedPixels && maskDataForPixelCopy) {
            const blurredMaskData = task.blurRadius > 0 ? await runGenerationPostprocess(maskDataForPixelCopy, { kind: 'blurAlpha', radius: task.blurRadius }) : maskDataForPixelCopy;
            const replicateImageData = resizeCtx.getImageData(0, 0, resizeCanvas.width, resizeCanvas.height);
            if (!replicateImageData) {
              console.error("processRepaintOnMask: replicateImageData is null after getImageData from resizeCtx.");
              URL.revokeObjectURL(replicateImageURL);
              if(maskImageURL) URL.revokeObjectURL(maskImageURL);
              return [];
            }

            outputImageData = outputCtx.createImageData(boundingBoxWidth, boundingBoxHeight);
            if (!outputImageData) {
              console.error("processRepaintOnMask: outputImageData is null after createImageData for output.");
              URL.revokeObjectURL(replicateImageURL);
              if(maskImageURL) URL.revokeObjectURL(maskImageURL);
              return [];
            }

            for (let y = 0; y < boundingBoxHeight; y++) {
              const yb = y * boundingBoxWidth;
              for (let x = 0; x < boundingBoxWidth; x++) {
                const outputPixelIndex = (yb + x) * 4;
                const maskPixelX = minX + x;
                const maskPixelY = minY + y;

                if (maskPixelX < maskCanvasForBounds.width && maskPixelY < maskCanvasForBounds.height && maskPixelX >= 0 && maskPixelY >= 0) {
                  const maskDataPixelIndex = (maskPixelY * maskCanvasForBounds.width + maskPixelX) * 4;
                  const blurredAlpha = blurredMaskData.data[maskDataPixelIndex + 3];
                  const replicatePixelIndex = (maskPixelY * resizeCanvas.width + maskPixelX) * 4;

                  if (outputPixelIndex >= 0 && outputPixelIndex < outputImageData.data.length &&
                    maskDataPixelIndex >= 0 && maskDataPixelIndex < blurredMaskData.data.length &&
                    replicatePixelIndex >= 0 && replicatePixelIndex < replicateImageData.data.length) {

                    outputImageData.data[outputPixelIndex]     = replicateImageData.data[replicatePixelIndex] ?? 0;
                    outputImageData.data[outputPixelIndex + 1] = replicateImageData.data[replicatePixelIndex + 1] ?? 0;
                    outputImageData.data[outputPixelIndex + 2] = replicateImageData.data[replicatePixelIndex + 2] ?? 0;
                    outputImageData.data[outputPixelIndex + 3] = blurredAlpha ?? 0;
                  } else {
                    outputImageData.data[outputPixelIndex + 3] = 0;
                    console.warn("processRepaintOnMask: Pixel index out of bounds during pixel copy.");
                  }
                } else {
                  outputImageData.data[outputPixelIndex + 3] = 0;
                }
              }
            }
            outputCtx.putImageData(outputImageData, 0, 0);

            if(outputImageData) {
                const taskProjectId = typeof task.projectId === 'string' ? task.projectId.trim() : '';
                const projectOwnerId = taskProjectId
                  ? kernel.current?.getInventory().Project.registry[taskProjectId]?.userId
                  : undefined;
                const cloudUrl = await uploadImageDataToCloud(outputImageData, 'webp', abortSignal, projectOwnerId);

                if (cloudUrl) {
                    uploadedMediaUrls = [cloudUrl];
                } else {
                    console.error("processRepaintOnMask: uploadImageDataToCloudStorage failed to return an cloud URL.");
                    URL.revokeObjectURL(replicateImageURL);
                    if(maskImageURL) URL.revokeObjectURL(maskImageURL);
                    return [];
                }
            } else {
                console.error("processRepaintOnMask: outputImageData is null, cannot upload.");
                URL.revokeObjectURL(replicateImageURL);
                if(maskImageURL) URL.revokeObjectURL(maskImageURL);
                return [];
            }

            if (uploadedMediaUrls.length > 0 && uploadedMediaUrls[0] && outputImageData) {
              const workspace = kernel.current?.getWorkspace(task.workspaceId);
              const frameRect = workspace?.frameRect || {x: 0, y: 0, w: 0, h: 0};
              const worldX = task.rect.x + minX / task.upscaleFactor;
              const worldY = task.rect.y + minY / task.upscaleFactor;

              task.layer = finalizeRasterLayerGeometry({
                id: createLayerId(),
                timestamp: Date.now(),
                imageData: outputImageData,
                rect: {
                  x: worldX - frameRect.x,
                  y: worldY - frameRect.y,
                  w: 0,
                  h: 0,
                },
                scale: 1.0 / task.upscaleFactor,
                visible: true,
                name: '',
                locked: true,
                opacity: 1,
                layerWorkspaceId: task.workspaceId,
                params : task.params,
                source: task.model,
                imageSrc: uploadedMediaUrls[0],
                contentVersion: Date.now(),
                reconstructedInputUrl: task.mainImagePrivateUrl ?? undefined,
                ...task.layerOverride
              });
            }

        } else {
            console.error("processRepaintOnMask: Output canvas context not available or hasMaskedPixels is false or no mask data.");
            URL.revokeObjectURL(replicateImageURL);
            if(maskImageURL) URL.revokeObjectURL(maskImageURL);
        }

    } catch (error) {
        if (isTaskCancellationError(error) || abortSignal?.aborted) {
            throw createTaskCancellationError(task.id);
        }
        console.error("processRepaintOnMask: Error processing and uploading masked repaint result:", error);
    }
    console.log("processRepaintOnMask: Function finished, returning uploadedMediaUrls", { uploadedMediaUrls });
    return uploadedMediaUrls;
  }, [kernel, finalizeRasterLayerGeometry, runGenerationPostprocess]);

  const processStretchOnArea = useCallback(async (replicateImageUrl: string, task: GenerationTask, abortSignal?: AbortSignal): Promise<string[]> => {
    let uploadedMediaUrls: string[] = [];
    if (!replicateImageUrl) return [];
    try {
        const replicateImageResponse = await retry(() => fetch(replicateImageUrl, { signal: abortSignal }), { retries: 5, delay: 5000, backoff: true, signal: abortSignal });
        if (!replicateImageResponse.ok) {
            console.error(`Failed to fetch replicate image: ${replicateImageUrl}`);
            return [];
        }
        const replicateImageBlob = await replicateImageResponse.blob();
        const replicateImageURL = URL.createObjectURL(replicateImageBlob);

        const imgReplicate = new Image();
        imgReplicate.crossOrigin = 'anonymous';
        imgReplicate.src = replicateImageURL;

        await new Promise(resolve => { imgReplicate.onload = resolve; });

        const taskProjectId = typeof task.projectId === 'string' ? task.projectId.trim() : '';
        const outputProject = kernel.current?.getInventory().Project.registry[taskProjectId];
        const dimensions = generatedImageDimensions(
          imgReplicate.naturalWidth, imgReplicate.naturalHeight, task.targetWidth, task.targetHeight,
          shouldPreserveGeneratedProportions(outputProject?.projectType, task),
        );
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = dimensions.width;
        tempCanvas.height = dimensions.height;
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) throw new Error("Canvas context not available");

        tempCtx.drawImage(imgReplicate, 0, 0, tempCanvas.width, tempCanvas.height);
        let imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);

        if (task.blurRadius > 0 && imageData && task.model !== 'ideogram-inpaint') {
          const blurredImageData = await runGenerationPostprocess(imageData, { kind: 'blurAlpha', radius: task.blurRadius });
          imageData = blurredImageData;
        }

        // Ideogram inpaint returns a full-frame result already alpha-clipped to the painted region
        // (transparent elsewhere). Crop it down to that patch so the resulting layer is just the
        // masked area, not the whole generation rect, and remember the offset to position it.
        let inpaintLayerOffsetX = 0;
        let inpaintLayerOffsetY = 0;
        if (task.model === 'ideogram-inpaint' && task.maskUrl) {
          // Ideogram returns the full edited frame. Cut it down to the painted region client-side:
          // draw the B/W mask scaled to the result and use it as the alpha channel (BLACK = edited
          // region -> opaque, WHITE -> transparent), then crop to the patch bounds so the new layer
          // is just the masked area, not the whole generation rect.
          try {
            const maskResp = await fetch(task.maskUrl, { signal: abortSignal });
            if (maskResp.ok) {
              const maskObjUrl = URL.createObjectURL(await maskResp.blob());
              const maskImg = new Image();
              maskImg.crossOrigin = 'anonymous';
              maskImg.src = maskObjUrl;
              await new Promise<void>((res, rej) => { maskImg.onload = () => res(); maskImg.onerror = () => rej(new Error('mask load failed')); });
              const maskCanvas = document.createElement('canvas');
              maskCanvas.width = imageData.width;
              maskCanvas.height = imageData.height;
              const mctx = maskCanvas.getContext('2d', { willReadFrequently: true });
              if (mctx) {
                mctx.drawImage(maskImg, 0, 0, imageData.width, imageData.height);
                const maskPixels = mctx.getImageData(0, 0, imageData.width, imageData.height).data;
                const id = imageData.data;
                for (let i = 0; i < id.length; i += 4) {
                  id[i + 3] = (maskPixels[i] ?? 255) < 128 ? 255 : 0;
                }
              }
              URL.revokeObjectURL(maskObjUrl);
            }
          } catch (maskErr) {
            console.warn('[ideogram-inpaint] failed to apply mask; keeping full frame', maskErr);
          }
          const cropped = cropImageDataToOpaqueBounds(imageData);
          if (cropped) {
            imageData = cropped.data;
            inpaintLayerOffsetX = cropped.offsetX;
            inpaintLayerOffsetY = cropped.offsetY;

            // Lissage: the mask alpha above is a hard 0/255 step, which shows up as a visible seam
            // where the patch meets the original below. Feather that edge into a gradient so the
            // patch blends in. blurAlphaChannel ramps the alpha inward across the boundary; sizing
            // the feather from the patch keeps it proportional and cheap (we feather the small crop,
            // not the full frame). task.blurRadius (0 by default) acts as a manual override.
            const minSide = Math.min(imageData.width, imageData.height);
            const auto = Math.max(4, Math.min(48, Math.round(minSide * 0.04)));
            const override = task.blurRadius > 0 ? task.blurRadius : auto;
            const featherPx = Math.min(Math.floor(minSide / 4), override);
            if (featherPx >= 1) {
              imageData = await runGenerationPostprocess(imageData, { kind: 'blurAlpha', radius: featherPx });
            }
          }
        }

        const projectOwnerId = taskProjectId
          ? kernel.current?.getInventory().Project.registry[taskProjectId]?.userId
          : undefined;
        const cloudUrl = await uploadImageDataToCloud(imageData, 'webp', abortSignal, projectOwnerId);

        if (cloudUrl) {
            uploadedMediaUrls = [cloudUrl]; // Wrap in array as before
        } else {
            console.error("uploadImageDataToCloud failed to return an cloud URL.");
            URL.revokeObjectURL(replicateImageURL);
            return [];
        }

        if (uploadedMediaUrls.length > 0 && uploadedMediaUrls[0]) {
          const workspace = kernel.current?.getWorkspace(task.workspaceId);
          const frameRect = workspace?.frameRect || {x: 0, y: 0, w: 0, h: 0};

          const params = task.params;
          task.layer = finalizeRasterLayerGeometry({
            id: createLayerId(),
            timestamp: Date.now(),
            imageData: imageData,
            rect: {
              x: (task.rect.x - frameRect.x) + inpaintLayerOffsetX / task.upscaleFactor,
              y: (task.rect.y - frameRect.y) + inpaintLayerOffsetY / task.upscaleFactor,
              w: 0,
              h: 0,
            },
            scale: 1.0 / (task.upscaleFactor),
            visible: (task.mergeStrategy === 'stretchOnArea'),
            name: '',
            locked: true,
            opacity: 1,
            layerWorkspaceId: task.workspaceId,
            params: (params as Partial<{ noParamsSave: boolean }> | undefined)?.noParamsSave ? undefined : task.params,
            source: task.model,
            imageSrc: uploadedMediaUrls[0],
            contentVersion: Date.now(),
            reconstructedInputUrl: task.mainImagePrivateUrl ?? undefined,
            ...task.layerOverride
          });
        }

        URL.revokeObjectURL(replicateImageURL); // Clean up object URL
    } catch (error) {
        if (isTaskCancellationError(error) || abortSignal?.aborted) {
            throw createTaskCancellationError(task.id);
        }
        console.error("Error processing stretchOnArea strategy image:", error);
        return [];
    }
    return uploadedMediaUrls;
  }, [kernel, finalizeRasterLayerGeometry, runGenerationPostprocess]);

  const isGenerationTaskCancelled = useCallback((task: GenerationTask, abortSignal?: AbortSignal): boolean => {
    if (abortSignal?.aborted) return true;
    const k = kernel.current;
    if (!k) return true;

    const taskSessionId =
      typeof task.chatSessionId === 'string' && task.chatSessionId.trim().length > 0
        ? task.chatSessionId.trim()
        : null;
    // Meme regle que cancelGenerationTasksForSession : le stop de la session n'emporte que ce
    // que l'agent a lance, pas un rendu manuel qui porte la session pour l'attribution.
    if (taskSessionId && task.isAgentSpawned === true
      && k.readEntityRuntimeState('ChatSession', taskSessionId).isStopRequested === true) {
      return true;
    }

    const taskProjectId = typeof task.projectId === 'string' ? task.projectId.trim() : '';
    if (!taskProjectId) return true;
    return !getGenerationTasks(taskProjectId).some((candidate) => candidate.id === task.id);
  }, [kernel, getGenerationTasks]);

  const throwIfGenerationTaskCancelled = useCallback((task: GenerationTask, abortSignal?: AbortSignal): void => {
    if (isGenerationTaskCancelled(task, abortSignal)) {
      throw createTaskCancellationError(task.id);
    }
  }, [isGenerationTaskCancelled]);

  const processGenerationTask = useCallback(async (
    task: GenerationTask,
    abortSignal?: AbortSignal,
    recoveredTerminal?: GenerationTaskStatusSocketPayload,
  ) => {
   if (handledGenerationTaskIdsRef.current.has(task.id)) return [];
   handledGenerationTaskIdsRef.current.add(task.id);

   let dbJobId: string | null = null;
   let providerJobId: string | null = null;
   let serverMaterialized = false;
   const outputScope = normalizeGenerationOutputScope(task.outputScope, {
     workspaceId: task.workspaceId,
     projectId: task.projectId,
   });
   const projectId = typeof task.projectId === 'string' ? task.projectId.trim() : '';
   // Decided once: the frame can change while Align and Harmonize run, and auto-integrate and the legacy harmonize below must agree.
   let legacyHarmonizeDecision: boolean | undefined;
   const legacyHarmonizeApplies = () => (legacyHarmonizeDecision ??= generationHarmonizeApplies(
     (task.params as Partial<{ harmonize: unknown }> | undefined)?.harmonize,
     kernel.current?.getWorkspace(task.workspaceId)?.frameRect,
     task.rect,
   ));
   let generationChange: GenerationChange | null = null;

   try {
      if (!projectId || !outputScope) {
        throw new Error(`Task ${task.id} is missing a valid output scope.`);
      }
      if (outputScope.kind === 'WORKSPACE' && task.workspaceId?.trim() !== outputScope.workspaceId) {
        throw new Error(`Task ${task.id} is missing a coherent workspace output scope.`);
      }
      if (outputScope.kind === 'PROJECT' && outputScope.projectId !== projectId) {
        throw new Error(`Task ${task.id} is outside its project output scope.`);
      }
      task.outputScope = outputScope;
      throwIfGenerationTaskCancelled(task, abortSignal);
      let createdVersionId: string | null = null;
      const input: GenerationTask = { ...task };
      delete input.maskFile;
      delete input.layer;
      delete input.mainImageData;
      delete (input as any).status;
      delete (input as any).internalJobId;

      const submissionStartedAt = Date.now();
      let response;
      try {
        if (recoveredTerminal) {
          response = {
            data: {
              jobId: recoveredTerminal.generationLogId ?? null,
              generationTaskId: recoveredTerminal.generationTaskId ?? null,
              providerJobId: recoveredTerminal.providerJobId ?? null,
              layerId: recoveredTerminal.layerId,
              layerIds: recoveredTerminal.layerIds,
              outputScope: recoveredTerminal.outputScope,
              result: normalizeSocketOutputUrls(recoveredTerminal),
            },
          };
        } else {
          response = await axios.post('/api/' + task.type, input, { signal: abortSignal, ...(task.outputScope?.kind === 'PROJECT' ? { timeout: 60_000 } : {}) });
          if (response.data.outputSavePending === true) {
            task.serverTaskId = response.data.generationTaskId;
            const saved = await waitForVideoSocketTerminal(task, null, submissionStartedAt, abortSignal, true);
            if (!saved || saved.status !== 'SUCCEEDED') throw Object.assign(new Error(saved?.errorMessage || 'The generated output could not be saved.'), { code: saved?.code, failureDetails: readImageGenerationFailureDetails(saved) });
            response = { data: { generationTaskId: saved.generationTaskId, jobId: saved.generationLogId, providerJobId: saved.providerJobId,
              layerId: saved.layerId, layerIds: saved.layerIds, outputScope: saved.outputScope, result: normalizeSocketOutputUrls(saved) } };
          }
        }
      } catch (submissionError) {
        const socketTerminal = await waitForVideoSocketTerminal(task, submissionError, submissionStartedAt, abortSignal);
        if (!socketTerminal) {
          throw submissionError;
        }
        if (socketTerminal.status === 'FAILED') {
          throw Object.assign(new Error(socketTerminal.errorMessage || 'Generation failed.'), { code: socketTerminal.code, failureDetails: readImageGenerationFailureDetails(socketTerminal) });
        }
        const outputUrls = normalizeSocketOutputUrls(socketTerminal);
        if (outputUrls.length === 0) {
          throw new Error('Video generation succeeded but no output URL was received from the socket event.');
        }
        response = {
          data: {
            jobId: socketTerminal.generationLogId ?? null,
            generationTaskId: socketTerminal.generationTaskId ?? null,
            providerJobId: socketTerminal.providerJobId ?? null,
            layerId: socketTerminal.layerId,
            layerIds: socketTerminal.layerIds,
            outputScope: socketTerminal.outputScope,
            result: outputUrls,
          },
        };
      }
      throwIfGenerationTaskCancelled(task, abortSignal);
      const serverAssignedTaskId =
        response &&
        response.data &&
        typeof response.data === 'object' &&
        response.data !== null &&
        typeof (response.data as { generationTaskId?: unknown }).generationTaskId === 'string'
          ? ((response.data as { generationTaskId: string }).generationTaskId.trim() || null)
          : null;

      if (serverAssignedTaskId) {
        task.serverTaskId = serverAssignedTaskId;
        setGenerationTasks(projectId, prevTasks =>
          prevTasks.map(t =>
            t.id === task.id ? { ...t, serverTaskId: serverAssignedTaskId } : t
          )
        );
      }
      if (!recoveredTerminal) emitGenerationTaskSpawnTimelineEvent(task, serverAssignedTaskId);
      throwIfGenerationTaskCancelled(task, abortSignal);
      let finalImageUrls: string[] = []; // Array to hold the final URL(s)

      // 3. Determine Flow: direct terminal result only. Legacy job polling is intentionally removed.
      if (response.data.jobId) {
        dbJobId = response.data.jobId;
      }
      if (typeof response.data.providerJobId === 'string' && response.data.providerJobId.trim().length > 0) {
        providerJobId = response.data.providerJobId.trim();
      }

      if (response.data.result) {
        throwIfGenerationTaskCancelled(task, abortSignal);
        console.log(`Task ${task.id}: Backend job completed (direct terminal result).`);
        finalImageUrls = response.data.result;
        if (!Array.isArray(finalImageUrls)) {
          console.warn(`Task ${task.id}: Backend result was not an array, wrapping.`);
          finalImageUrls = finalImageUrls ? [finalImageUrls] : [];
        }
      } else if (response.data.jobId) {
        console.error(`Task ${task.id}: Backend returned jobId without result after legacy polling removal.`, response.data);
        throw new Error('Invalid response from backend: jobId without terminal result is no longer supported.');
      } else {
        console.error(`Task ${task.id}: Invalid response from backend. Missing 'result'.`, response.data);
        throw new Error('Invalid response from backend.');
      }

      throwIfGenerationTaskCancelled(task, abortSignal);

      if (finalImageUrls.length === 0) {
        console.warn(`Task ${task.id}: No image URLs obtained after processing.`);
        setGenerationTasks(projectId, prevTasks => prevTasks.map(t => t.id === task.id ? { ...t /* , status: 'COMPLETE_NO_IMG' */ } : t));
        return;
      }

      // --- 5. Process Merge Strategies using finalImageUrls ---
      throwIfGenerationTaskCancelled(task, abortSignal);
      let uploadedMediaUrls: string[] = [];
      let generatedAudioLayer: Layer | null = null;
      let mireloPostprocessFailure: Error | null = null;
      const primaryImageUrl = finalImageUrls[0];
      if(!primaryImageUrl) throw 'Missing url';

      const projectOwnerId = kernel.current?.getInventory().Project.registry[projectId]?.userId;

      if (outputScope.kind === 'PROJECT' && (task.type === 'diffuse' || task.type === 'film')) {
        if (!kernel.current) throw new Error('Kernel is unavailable for saved output hydration.');
        serverMaterialized = true;
        const layers = await adoptProjectGeneration(kernel.current, task, response.data);
        if (task.type === 'diffuse') void warnIfUndecodable(layers, (message) => kernel.current?.handleError(message));
        task.layer = layers[0];
        uploadedMediaUrls = finalImageUrls;
      } else if (task.operation === 'vectorize-layer' || task.operation === 'vectorize-workspace' || isVectorOutputStudioModel(task.model)) {
        const sourceLayerId = typeof task.sourceLayerId === 'string' ? task.sourceLayerId.trim() : '';
        const sourceLayer = sourceLayerId ? kernel.current?.getLayerRegistry()[sourceLayerId] : null;
        if (task.operation === 'vectorize-layer' && !sourceLayer) {
          throw new Error('Vectorization source layer is no longer available.');
        }

        const workspace = kernel.current?.getWorkspace(task.workspaceId);
        const frameRect = workspace?.frameRect || { x: 0, y: 0, w: 0, h: 0 };
        const fallbackRect: Rect = {
          x: task.rect.x - frameRect.x,
          y: task.rect.y - frameRect.y,
          w: task.rect.w,
          h: task.rect.h,
        };
        const layerRect = sourceLayer?.rect ?? fallbackRect;
        const fallbackIntrinsicWidth = Math.max(1, Math.round(task.targetWidth || layerRect.w || sourceLayer?.imageData?.width || 1));
        const fallbackIntrinsicHeight = Math.max(1, Math.round(task.targetHeight || layerRect.h || sourceLayer?.imageData?.height || 1));

        const svgMarkup = await resolveSvgLayerInlineMarkup({ imageSrc: primaryImageUrl });
        throwIfGenerationTaskCancelled(task, abortSignal);
        const intrinsic = svgMarkup
          ? parseSvgIntrinsicSize(svgMarkup)
          : await loadSvgIntrinsicSizeFromUrl(primaryImageUrl, {
              width: fallbackIntrinsicWidth,
              height: fallbackIntrinsicHeight,
            });

        // A generated vector keeps its own aspect inside the generation area; a trace keeps its source's rect.
        const placedRect = isVectorModelId(task.model) && intrinsic.width > 0 && intrinsic.height > 0
          ? fitRectInside(layerRect, intrinsic.width / intrinsic.height)
          : layerRect;
        const workspaceLayers = kernel.current?.getLayersInWorkspaceContext(task.workspaceId) ?? [];
        const layerName = typeof task.layerOverride?.name === 'string' && task.layerOverride.name.trim().length > 0
          ? task.layerOverride.name
          : (sourceLayer
            ? `${sourceLayer.name || 'Layer'} Vectorised`
            : isVectorModelId(task.model) ? 'Vector' : `${workspace?.name || 'Workspace'} Vectorised`);
        const sourceScale = sourceLayer?.scale;
        const vectorScale = typeof sourceScale === 'number' && Number.isFinite(sourceScale) && sourceScale > 0
          ? sourceScale
          : (intrinsic.width > 0 && placedRect.w > 0 ? placedRect.w / intrinsic.width : 1);
        const nowIso = new Date().toISOString();

        task.layer = {
          id: createLayerId(),
          timestamp: Date.now(),
          name: layerName,
          visible: true,
          type: 'svg',
          imageSrc: primaryImageUrl,
          imageData: null,
          source: 'vectorized',
          rect: { ...placedRect },
          scale: vectorScale,
          // A trace inherits its source's lock; a generated vector is there to be edited.
          locked: isVectorModelId(task.model) ? false : (sourceLayer?.locked ?? true),
          opacity: sourceLayer?.opacity ?? 1,
          layerWorkspaceId: task.workspaceId,
          renderOrder: getNextRenderOrderForWorkspace(workspaceLayers, task.workspaceId),
          params: {
            ...(sourceLayer?.params && typeof sourceLayer.params === 'object' ? sourceLayer.params : {}),
            [SVG_LAYER_PARAM_KEY]: {
              ...(svgMarkup ? { svg: svgMarkup } : {}),
              url: primaryImageUrl,
              width: intrinsic.width,
              height: intrinsic.height,
              viewBox: intrinsic.viewBox,
              source: 'vectorized',
            },
          },
          isVectorizing: false,
          contentVersion: Date.now(),
          modificationLog: sourceLayer
            ? appendLayerModificationLog(sourceLayer, {
                timestamp: nowIso,
                type: 'MANUAL_VECTORIZE',
                description: `Created vectorized SVG layer from "${sourceLayer.name || sourceLayer.id}".`,
              })
            : [{
                timestamp: nowIso,
                type: 'MANUAL_VECTORIZE',
                description: `Created vectorized SVG layer from "${workspace?.name || task.workspaceId}".`,
              }],
          ...task.layerOverride,
        };
        uploadedMediaUrls = [primaryImageUrl];
      } else if (task.mergeStrategy === 'addInList') {
        try {
            const mp4Response = await retry(
              () => axios.get(primaryImageUrl, { responseType: 'blob', signal: abortSignal }),
              { retries: 5, delay: 5000, backoff: true, signal: abortSignal }
            );
            throwIfGenerationTaskCancelled(task, abortSignal);
            const videoBlob = mp4Response.data;
            const cloudUrl = await uploadFileToCloud(videoBlob, Date.now() + '.mp4', 'video/mp4', abortSignal, projectOwnerId);
            throwIfGenerationTaskCancelled(task, abortSignal);
            if (!cloudUrl) throw new Error('Upload to cloud failed for video.');

            let videoFramesUrls: string[] = [];
            let outputWidth = 512; // Fallback width
            let outputHeight = 512; // Fallback height

            try {
                const frameExtractionResult = await extractFramesFromVideo(videoBlob);
                throwIfGenerationTaskCancelled(task, abortSignal);
                if (frameExtractionResult) {
                    outputWidth = frameExtractionResult.width;
                    outputHeight = frameExtractionResult.height;

                    // Upload all extracted frames in parallel
                    const uploadPromises = frameExtractionResult.frames.map(frameData =>
                        uploadFileToCloud(frameData.frameBlob, `${Date.now()}-frame-${frameData.timestamp.toFixed(2)}.webp`, 'image/webp', abortSignal, projectOwnerId)
                    );
                    const uploadedUrls = await Promise.all(uploadPromises);
                    throwIfGenerationTaskCancelled(task, abortSignal);
                    videoFramesUrls = uploadedUrls.filter((url): url is string => url !== null);
                }
            } catch (frameError) {
                throwIfGenerationTaskCancelled(task, abortSignal);
                console.error("Could not extract video frames or get dimensions:", frameError);
                // Proceed with fallback dimensions
            }

            const generationArea = task.rect;
            const scaleX = generationArea.w / outputWidth;
            const scaleY = generationArea.h / outputHeight;
            const finalScale = Math.min(scaleX, scaleY);
            const finalWidth = outputWidth * finalScale;
            const finalHeight = outputHeight * finalScale;
            const finalX = generationArea.x + (generationArea.w - finalWidth) / 2;
            const finalY = generationArea.y + (generationArea.h - finalHeight) / 2;

            const workspace = kernel.current?.getWorkspace(task.workspaceId);
            const frameRect = workspace?.frameRect || {x: 0, y: 0, w: 0, h: 0};

            const newVideoLayer: Layer = finalizeRasterLayerGeometry({
                id: createLayerId(),
                timestamp: Date.now(),
                params: (task.params as Partial<{ noParamsSave: boolean }> | undefined)?.noParamsSave ? undefined : task.params,
                source: task.model,
                videoSrc: cloudUrl,
                videoFramesUrls: videoFramesUrls,
                name: task.layerOverride?.name || nameGeneratedResult(kernel.current, task.workspaceId, task, 'video'),
                type: 'video',
                rect: {
                  x: finalX - frameRect.x,
                  y: finalY - frameRect.y,
                  w: 0,
                  h: 0,
                },
                scale: finalScale,
                visible: true,
                locked: true,
                opacity: 1,
                layerWorkspaceId: task.workspaceId,
                imageData: null,
                reconstructedInputUrl: task.mainImagePrivateUrl ?? undefined,
                ...task.layerOverride
            }, { width: outputWidth, height: outputHeight });

            task.layer = newVideoLayer;
            uploadedMediaUrls = [cloudUrl];

            if (task.model === 'mirelo-sfx-1.6') {
              try {
                const extractedAudioSrc = await extractAudioFromVideo({
                  sourceSrc: cloudUrl,
                  timeoutMs: MIRELO_AUDIO_EXTRACT_TIMEOUT_MS,
                  abortSignal,
                });
                throwIfGenerationTaskCancelled(task, abortSignal);
                const separatedLayers = buildMireloSeparatedLayers({
                  carrierLayer: newVideoLayer,
                  audioSrc: extractedAudioSrc,
                  audioLayerId: createLayerId(),
                  timestamp: Date.now(),
                });
                task.layer = separatedLayers.carrierLayer;
                generatedAudioLayer = separatedLayers.audioLayer;
                uploadedMediaUrls.push(extractedAudioSrc);
              } catch (audioExtractionError) {
                if (isTaskCancellationError(audioExtractionError) || abortSignal?.aborted) {
                  throw audioExtractionError;
                }
                task.layer = buildMireloRecoveryCarrier(newVideoLayer);
                mireloPostprocessFailure = new Error(
                  `Mirelo SFX generation completed, but soundtrack separation failed. The generated carrier was kept in the timeline: use “Separate Audio” on that video track to retry extraction. ${
                    audioExtractionError instanceof Error ? audioExtractionError.message : String(audioExtractionError)
                  }`,
                  { cause: audioExtractionError },
                );
              }
            }

        } catch (downloadOrUploadError: any) {
            if (isTaskCancellationError(downloadOrUploadError) || abortSignal?.aborted) {
              throw downloadOrUploadError;
            }
            console.error(`Task ${task.id} (Job ${dbJobId}): Error downloading/uploading video:`, downloadOrUploadError);
            throw new Error(downloadOrUploadError.message || 'Failed to process video output.');
        }
      } else if (task.mergeStrategy === 'repaintOnMask') {
        const urls = await processRepaintOnMask(primaryImageUrl, task, abortSignal);
        uploadedMediaUrls.push(...urls);
      } else if ((task.mergeStrategy === 'stretchOnArea') || (task.mergeStrategy === 'alignOnArea')) {
        const urls = await processStretchOnArea(primaryImageUrl, task, abortSignal);
        uploadedMediaUrls.push(...urls);

        // New tasks use explicit post-process intent; legacy tasks keep alignOnArea compatibility.
        const shouldAutoIntegrate = task.autoIntegrateWithUnderlying === true || task.mergeStrategy === 'alignOnArea';

        if (shouldAutoIntegrate && task.layer && kernel.current && task.layerContextSnapshot) {
          const layerToIntegrate = task.layer;
          // The generation's own harmonize parameter recolours the layer later on: a second Harmonize here would stack on it.
          const harmonize = !legacyHarmonizeApplies() && task.autoColorMatch === true;
          console.log(`[Task ${task.id}] Post-generation integration triggered for new layer ${layerToIntegrate.id} (harmonize: ${harmonize}).`);
          const endAutoIntegrate = startPostprocessPerf(task, harmonize ? 'auto-integrate' : 'auto-align');
          try {
            // Reconstruct the stable underlying context from the task's snapshot to prevent race conditions
            const snapshotLayerIds = new Set(task.layerContextSnapshot.map(item => item.id));
            const underlyingLayersOverride = [...snapshotLayerIds]
              .map((layerId) => kernel.current?.getLayerRegistry()[layerId])
              .filter((layer): layer is Layer => Boolean(layer));

            const integrated = await kernel.current.integrateGeneratedLayer(layerToIntegrate, underlyingLayersOverride, harmonize);

            task.layer = integrated.layer;
            generationChange = integrated.change;
          } catch (err) {
            console.error(`[Task ${task.id}] Post-generation integration failed, falling back to stretched result:`, err);
          } finally {
            endAutoIntegrate();
          }
        }
      } else if ((task.mergeStrategy === 'maskArea')) {
        // Original logic for maskArea tasks
            const replicateImageUrl = primaryImageUrl;
            if (!replicateImageUrl) return [];

            try {
              const replicateImageResponse = await retry(
                () => fetch(replicateImageUrl, { signal: abortSignal }),
                { retries: 5, delay: 5000, backoff: true, signal: abortSignal }
              );
              throwIfGenerationTaskCancelled(task, abortSignal);
              if (!replicateImageResponse.ok) {
                  console.error(`Failed to fetch replicate image: ${replicateImageUrl}`);
                  return [];
              }
              const replicateImageBlob = await replicateImageResponse.blob();
              throwIfGenerationTaskCancelled(task, abortSignal);
              const replicateImageURL = URL.createObjectURL(replicateImageBlob);

              const imgReplicate = new Image();
              imgReplicate.crossOrigin = 'anonymous';
              imgReplicate.src = replicateImageURL;

              await new Promise(resolve => { imgReplicate.onload = resolve; });

              const resizeCanvas = document.createElement('canvas');
              resizeCanvas.width = task.targetWidth;
              resizeCanvas.height = task.targetHeight;
              const resizeCtx = resizeCanvas.getContext('2d');
              if (!resizeCtx) throw new Error("Canvas context not available");

              resizeCtx.drawImage(imgReplicate, 0, 0, resizeCanvas.width, resizeCanvas.height);
              const imageData = resizeCtx.getImageData(0, 0, resizeCanvas.width, resizeCanvas.height);


              // Process the image to be white and transparent
              for (let i = 0; i < imageData.data.length; i += 4) {
                  const r = imageData.data[i] ?? 0;
                  const g = imageData.data[i + 1] ?? 0;
                  const b = imageData.data[i + 2] ?? 0;

                  // Check if pixel is close to black (adjust threshold as needed)
                  if (r < 50 && g < 50 && b < 50) {
                      imageData.data[i + 3] = 0; // Set alpha to 0 for transparent
                  } else {
                      imageData.data[i] = 255;     // Set to white
                      imageData.data[i + 1] = 255;
                      imageData.data[i + 2] = 255;
                      imageData.data[i + 3] = 255; // Set alpha to 255 for opaque white
                  }
              }
              resizeCtx.putImageData(imageData, 0, 0);
              const processedImageData = resizeCtx.getImageData(0, 0, resizeCanvas.width, resizeCanvas.height);


              let minX = resizeCanvas.width;
              let minY = resizeCanvas.height;
              let maxX = 0;
              let maxY = 0;
              let hasWhitePixels = false;


              for (let y = 0; y < resizeCanvas.height; y++) {
                  for (let x = 0; x < resizeCanvas.width; x++) {
                      const pixelIndex = (y * resizeCanvas.width + x) * 4;
                      if (processedImageData.data[pixelIndex] === 255) { // Check if it's white (R, G, B all 255)
                          hasWhitePixels = true;
                          minX = Math.min(minX, x);
                          minY = Math.min(minY, y);
                          maxX = Math.max(maxX, x);
                          maxY = Math.max(maxY, y);
                      }
                  }
              }


              let outputImageData: ImageData | null = null;
              if (hasWhitePixels) {
                  const boundingBoxWidth = Math.max(1, Math.ceil(maxX - minX));
                  const boundingBoxHeight = Math.max(1, Math.ceil(maxY - minY));

                  const outputCanvas = document.createElement('canvas');
                  outputCanvas.width = boundingBoxWidth;
                  outputCanvas.height = boundingBoxHeight;
                  const outputCtx = outputCanvas.getContext('2d');
                  if(outputCtx) {
                      outputImageData = outputCtx.getImageData(0, 0, boundingBoxWidth, boundingBoxHeight);
                      const croppedSourceData = resizeCtx.getImageData(minX, minY, boundingBoxWidth, boundingBoxHeight);
                      outputCtx.putImageData(croppedSourceData, 0, 0);
                      outputImageData = outputCtx.getImageData(0, 0, boundingBoxWidth, boundingBoxHeight);
                  }
              } else {
                  outputImageData = resizeCtx.createImageData(1, 1); // Create a minimal transparent image if no white pixels found.
              }


              let cloudUrl : string | null = null;
              if(outputImageData){
                cloudUrl = await uploadImageDataToCloud(outputImageData, 'webp', abortSignal, projectOwnerId);
                throwIfGenerationTaskCancelled(task, abortSignal);
              }


              if (cloudUrl) {
                  uploadedMediaUrls = [cloudUrl];
              } else {
                  console.error("uploadImageDataToCloudStorage failed to return an cloud URL.");
                  URL.revokeObjectURL(replicateImageURL);
                  return [];
              }

              if (uploadedMediaUrls.length > 0 && uploadedMediaUrls[0] && outputImageData) {
                const workspace = kernel.current?.getWorkspace(task.workspaceId);
                const frameRect = workspace?.frameRect || {x: 0, y: 0, w: 0, h: 0};
                const worldX = task.rect.x + minX / task.upscaleFactor;
                const worldY = task.rect.y + minY / task.upscaleFactor;

                task.layer = finalizeRasterLayerGeometry({
                  id: createLayerId(),
                  timestamp: Date.now(),
                  imageData: outputImageData,
                  rect: {
                    x: worldX - frameRect.x,
                    y: worldY - frameRect.y,
                    w: 0,
                    h: 0,
                  },
                  scale: 1.0 / task.upscaleFactor,
                  visible: false, // Mask area is initially not visible
                  name: ('mask: ' + task.params?.prompt) || '',
                  locked: true,
                  opacity: 1,
                  layerWorkspaceId: task.workspaceId,
                  params: (task.params as Partial<{ noParamsSave: boolean }> | undefined)?.noParamsSave ? undefined : task.params,
                  source: task.model,
                  imageSrc: uploadedMediaUrls[0],
                  contentVersion: Date.now(),
                  reconstructedInputUrl: task.mainImagePrivateUrl ?? undefined,
                  ...task.layerOverride
                });

                void task.layer;
              }

              URL.revokeObjectURL(replicateImageURL); // Clean up object URL

            } catch (error) {
                console.error("Error processing maskArea strategy image:", error);
                return [];
            }
      } else if ((task.mergeStrategy === 'fitInArea')) {
        const replicateImageUrl = primaryImageUrl;
        if (!replicateImageUrl) return [];

        try {
          const replicateImageResponse = await retry(
            () => fetch(replicateImageUrl, { signal: abortSignal }),
            { retries: 5, delay: 5000, backoff: true, signal: abortSignal }
          );
          throwIfGenerationTaskCancelled(task, abortSignal);
          if (!replicateImageResponse.ok) {
              console.error(`Failed to fetch replicate image: ${replicateImageUrl}, status: ${replicateImageResponse.status}, statusText: ${replicateImageResponse.statusText}`);
              return [];
          }
          const replicateImageBlob = await replicateImageResponse.blob();
          throwIfGenerationTaskCancelled(task, abortSignal);
          const replicateImageURL = URL.createObjectURL(replicateImageBlob);

          const imgReplicate = new Image();
          imgReplicate.crossOrigin = 'anonymous';
          imgReplicate.src = replicateImageURL;

          await new Promise<void>(resolve => { imgReplicate.onload = () => {
            resolve();
          }; });

          const replicateImageWidth = imgReplicate.naturalWidth;
          const replicateImageHeight = imgReplicate.naturalHeight;

          const scale = Math.min(task.targetWidth / replicateImageWidth, task.targetHeight / replicateImageHeight);

          const offsetX = (task.targetWidth - replicateImageWidth * scale) / 2;
          const offsetY = (task.targetHeight - replicateImageHeight * scale) / 2;

          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = replicateImageWidth;
          tempCanvas.height = replicateImageHeight;
          const tempCtx = tempCanvas.getContext('2d');
          if (!tempCtx) {
            console.error("Canvas context not available for tempCanvas");
            URL.revokeObjectURL(replicateImageURL);
            throw new Error("Canvas context not available");
          }
          tempCtx.drawImage(imgReplicate, 0, 0, replicateImageWidth, replicateImageHeight);
          let imageData = tempCtx.getImageData(0, 0, replicateImageWidth, replicateImageHeight);
          
          if (task.blurRadius > 0 && imageData) {
            console.log(`Applying alpha blur (radius: ${task.blurRadius}) to fitInArea image.`);
            const blurredImageData = await runGenerationPostprocess(imageData, { kind: 'blurAlpha', radius: task.blurRadius });
            imageData = blurredImageData;
          }

          const cloudUrl = await uploadImageDataToCloud(imageData, 'webp', abortSignal, projectOwnerId);
          throwIfGenerationTaskCancelled(task, abortSignal);

          if (cloudUrl) {
              uploadedMediaUrls = [cloudUrl];
          } else {
              console.error("uploadImageDataToCloudStorage failed to return an cloud URL.");
              URL.revokeObjectURL(replicateImageURL);
              return [];
          }

          if (uploadedMediaUrls.length > 0 && uploadedMediaUrls[0]) {
            const workspace = kernel.current?.getWorkspace(task.workspaceId);
            const frameRect = workspace?.frameRect || {x: 0, y: 0, w: 0, h: 0};
            const worldX = task.rect.x + offsetX / task.upscaleFactor;
            const worldY = task.rect.y + offsetY / task.upscaleFactor;

            task.layer = finalizeRasterLayerGeometry({
              id: createLayerId(),
              timestamp: Date.now(),
              imageData: imageData,
              rect: {
                x: worldX - frameRect.x,
                y: worldY - frameRect.y,
                w: 0,
                h: 0,
              },
              scale: scale,
              visible: true,
              name: '',
              locked: true,
              opacity: 1,
              layerWorkspaceId: task.workspaceId,
              params : task.params,
              source: task.model,
              imageSrc: uploadedMediaUrls[0],
              contentVersion: Date.now(),
              reconstructedInputUrl: task.mainImagePrivateUrl ?? undefined,
              ...task.layerOverride
            });
          }

          URL.revokeObjectURL(replicateImageURL);

        } catch (error) {
            console.error("Error processing fitInArea strategy image:", error);
            return [];
        }
      } else if(task.mergeStrategy === 'combineWithLayer') {
        const replicateImageUrl = primaryImageUrl;
        if (!replicateImageUrl) return [];

        const layerImageFile = task.maskFile;
        if (!layerImageFile) {
          console.error("combineWithLayer: layerImageFile is missing for combineWithLayer strategy.");
          return [];
        }

        try {
            const replicateImageResponse = await retry(
              () => fetch(replicateImageUrl, { signal: abortSignal }),
              { retries: 5, delay: 5000, backoff: true, signal: abortSignal }
            );
            throwIfGenerationTaskCancelled(task, abortSignal);
            if (!replicateImageResponse.ok) {
                console.error(`Failed to fetch replicate image: ${replicateImageUrl}`);
                return [];
            }
            const replicateImageBlob = await replicateImageResponse.blob();
            throwIfGenerationTaskCancelled(task, abortSignal);
            const replicateImageURL = URL.createObjectURL(replicateImageBlob);

            const imgReplicate = new Image();
            imgReplicate.crossOrigin = 'anonymous';
            imgReplicate.src = replicateImageURL;

            const imgLayer = new Image();
            imgLayer.src = URL.createObjectURL(layerImageFile);


            await Promise.all([
              new Promise(resolve => { imgReplicate.onload = resolve; }),
              new Promise(resolve => { imgLayer.onload = resolve; })
            ]);

            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = task.targetWidth;
            tempCanvas.height = task.targetHeight;
            const tempCtx = tempCanvas.getContext('2d');
            if (!tempCtx) throw new Error("Canvas context not available");

            tempCtx.drawImage(imgReplicate, 0, 0, tempCanvas.width, tempCanvas.height);
            const generatedImageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);


            const layerCanvas = document.createElement('canvas');
            layerCanvas.width = task.targetWidth;
            layerCanvas.height = task.targetHeight;
            const layerCtx = layerCanvas.getContext('2d');
            if (!layerCtx) throw new Error("Layer canvas context not available");
            layerCtx.drawImage(imgLayer, 0, 0, task.targetWidth, task.targetHeight);
            const layerImageData = layerCtx.getImageData(0, 0, task.targetWidth, task.targetHeight);

            for (let i = 0; i < generatedImageData.data.length; i += 4) {
              if ((layerImageData.data[i + 3] || 0) > 128) {
                generatedImageData.data[i + 3] = 255;
              } else {
                generatedImageData.data[i + 3] = 0;
              }
            }
            tempCtx.putImageData(generatedImageData, 0, 0);
            const finalImageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);

            const cloudUrl = await uploadImageDataToCloud(finalImageData, 'webp', abortSignal, projectOwnerId);
            throwIfGenerationTaskCancelled(task, abortSignal);

            if (cloudUrl) {
                uploadedMediaUrls = [cloudUrl]; // Wrap in array as before
            } else {
                console.error("uploadImageDataToCloudStorage failed to return an cloud URL.");
                URL.revokeObjectURL(replicateImageURL);
                return [];
            }

            if (uploadedMediaUrls.length > 0 && uploadedMediaUrls[0]) {
              const workspace = kernel.current?.getWorkspace(task.workspaceId);
              const frameRect = workspace?.frameRect || {x: 0, y: 0, w: 0, h: 0};

              task.layer = finalizeRasterLayerGeometry({
                id: createLayerId(),
                timestamp: Date.now(),
                imageData: finalImageData,
                rect: {
                  x: task.rect.x - frameRect.x,
                  y: task.rect.y - frameRect.y,
                  w: 0,
                  h: 0,
                },
                scale: 1.0 / (task.upscaleFactor),
                visible: true,
                name: '',
                locked: true,
                opacity: 1,
                layerWorkspaceId: task.workspaceId,
                params: (task.params as Partial<{ noParamsSave: boolean }> | undefined)?.noParamsSave ? undefined : task.params,
                source: task.model,
                imageSrc: uploadedMediaUrls[0],
                contentVersion: Date.now(),
                reconstructedInputUrl: task.mainImagePrivateUrl ?? undefined,
                ...task.layerOverride
              });
            }

            URL.revokeObjectURL(replicateImageURL); // Clean up object URL
        } catch (error) {
            console.error("Error processing default strategy image:", error);
            return [];
        }

      } 

      throwIfGenerationTaskCancelled(task, abortSignal);

      const endPostprocessTotal = startPostprocessPerf(task, 'postprocess-total');
      try {
        if (task.layer && kernel.current) {
          const params = task.params;
          const harmonizeMode = (params as Partial<{ harmonize: 'On' | 'Auto' | 'Off' }> | undefined)?.harmonize || 'Off';
          const harmonizeProfile: 'soft' | 'strict' =
            (params as Partial<{ harmonize_profile: 'soft' | 'strict' }> | undefined)?.harmonize_profile === 'strict' ? 'strict' : 'soft';

          if (harmonizeMode === 'On' || harmonizeMode === 'Auto') {
            const endHarmonize = startPostprocessPerf(task, `harmonize-${harmonizeMode.toLowerCase()}-${harmonizeProfile}`);
            try {
              const shouldHarmonize = legacyHarmonizeApplies();
              if (harmonizeMode === 'Auto') {
                console.log(`[Task ${task.id}] Harmonize: 'Auto' ${shouldHarmonize ? 'on partial frame. Applying' : 'on full frame. Skipping'} color harmonization.`);
              }

              if (shouldHarmonize) {
                if (task.mainImageData && task.layer.imageData) {
                  console.log(`[Task ${task.id}] Direct color harmonization using source pixels.`);
                  const cv = await cvReadyPromise;
                  const harmonizedData = await performColorHarmonization(
                    resizeImageDataToPixelBudget(task.mainImageData, MAX_COLOR_HARMONIZATION_ANALYSIS_PIXELS),
                    resizeImageDataToPixelBudget(task.layer.imageData, MAX_COLOR_HARMONIZATION_ANALYSIS_PIXELS),
                    cv,
                    harmonizeProfile,
                    task.layer.imageData,
                  );
                  if (harmonizedData) {
                    task.layer.imageData = harmonizedData;
                    task.layer.imageSrc = undefined;
                  }
                } else {
                  if (harmonizeMode === 'On') console.log(`[Task ${task.id}] Harmonize: 'On'. Applying standard color harmonization.`);
                  const harmonizedLayer = await kernel.current.harmonizeLayerColor(task.layer, false, harmonizeProfile);
                  if (harmonizedLayer) task.layer = harmonizedLayer;
                }
              }
            } finally {
              endHarmonize();
            }
          }
        }

        const layerFinishJob: Extract<Parameters<typeof computeGenerationPostprocess>[1], { kind: 'layerFinish' }> = { kind: 'layerFinish' };

        // Post-processing: Mask out areas where underlying content is higher resolution
        const params = task.params as BaseParams | undefined;
        if (params?.preserve_high_res && task.layer && task.layer.imageData && kernel.current) {
          const endPreserveHighRes = startPostprocessPerf(task, 'preserve-high-res-prepare');
          try {
            const targetScale = 1.0 / task.upscaleFactor;
            const workspace = kernel.current.getWorkspace(task.workspaceId);
            if (workspace) {
              layerFinishJob.highRes = {
                frameRect: workspace.frameRect,
                targetRect: task.rect,
                targetScale,
                layers: selectHighResMaskingLayers(kernel.current.getLayersInWorkspaceContext(task.workspaceId), workspace.frameRect, task.rect),
              };
            }
            task.layer.imageSrc = undefined;
          } finally {
            endPreserveHighRes();
          }
        }

        const configuredAlphaRadius = (task.params as BaseParams)?.alpha_radius ?? 0;
        const alphaRadius = task.layerConsolidation?.tileEdgeFadeRadiusMode === 'tileOverlapMargin'
          ? resolveTileOverlapFadeRadius(task)
          : configuredAlphaRadius;
        if (alphaRadius > 0 && task.layer && task.layer.imageData && kernel.current) {
          const endAlphaFade = startPostprocessPerf(task, `alpha-fade-${alphaRadius}-prepare`);
          try {
            const workspace = kernel.current.getWorkspace(task.workspaceId);
            if (workspace) {
              const tolerance = 2;
              const frame = workspace.frameRect;
              const gen = task.rect;

              const touchesLeft = Math.abs(gen.x - frame.x) <= tolerance;
              const touchesTop = Math.abs(gen.y - frame.y) <= tolerance;
              const touchesRight = Math.abs((gen.x + gen.w) - (frame.x + frame.w)) <= tolerance;
              const touchesBottom = Math.abs((gen.y + gen.h) - (frame.y + frame.h)) <= tolerance;

              layerFinishJob.edgeFade = {
                radius: alphaRadius,
                sides: {
                  left: !touchesLeft,
                  top: !touchesTop,
                  right: !touchesRight,
                  bottom: !touchesBottom
                },
              };
              
              // The fade replaces the stored pixels, so the save uploads the faded ones.
              task.layer.imageSrc = undefined;
            }
          } finally {
            endAlphaFade();
          }
        }

        if ((layerFinishJob.highRes || layerFinishJob.edgeFade) && task.layer?.imageData) {
          task.layer.imageData = await runGenerationPostprocess(task.layer.imageData, layerFinishJob);
        }
      } finally {
        endPostprocessTotal();
      }

      if (task.layer && task.layer.type !== 'svg') {
        task.layer = finalizeRasterLayerGeometry(task.layer);
      }
      throwIfGenerationTaskCancelled(task, abortSignal);

      const layerConsolidation = task.layerConsolidation;
      const consolidationBatchId =
        typeof layerConsolidation?.batchId === 'string' ? layerConsolidation.batchId.trim() : '';
      const consolidationTileIndex =
        typeof layerConsolidation?.tileIndex === 'number' && Number.isFinite(layerConsolidation.tileIndex)
          ? layerConsolidation.tileIndex
          : null;
      const consolidationTileCount =
        typeof layerConsolidation?.tileCount === 'number' && Number.isFinite(layerConsolidation.tileCount)
          ? layerConsolidation.tileCount
          : null;
      const hasBatchAnchorConsolidation =
        task.outputScope?.kind === 'WORKSPACE' &&
        layerConsolidation?.mode === 'mergeIntoBatchAnchor' &&
        consolidationBatchId.length > 0 &&
        consolidationTileIndex !== null;
      const isConsolidationAnchorTile = hasBatchAnchorConsolidation && consolidationTileIndex === 0;

      let mergedIntoBatchAnchor = false;
      let completedGridBatch = false;
      const gridSections = hasBatchAnchorConsolidation ? layerConsolidation?.grid?.sections : undefined;

      if (gridSections && task.layer?.imageData && task.workspaceId && consolidationTileIndex !== null && kernel.current) {
        const k = kernel.current;
        const workspaceId = task.workspaceId;
        const frameRect = k.getWorkspace(workspaceId)?.frameRect;
        if (!frameRect) throw new Error(`Workspace "${workspaceId}" not found when painting a grid tile.`);
        const tileScale = task.layer.scale ?? 1;
        const tileRect: Rect = {
          x: frameRect.x + task.layer.rect.x,
          y: frameRect.y + task.layer.rect.y,
          w: task.layer.imageData.width * tileScale,
          h: task.layer.imageData.height * tileScale,
        };
        const touchedSections = gridSections.sections.filter(section => (
          section.rect.x < tileRect.x + tileRect.w && tileRect.x < section.rect.x + section.rect.w
          && section.rect.y < tileRect.y + tileRect.h && tileRect.y < section.rect.y + section.rect.h
        ));
        const sectionLayerFor = (sectionIndex: number) => (
          findGridBatchLayers(k.getLayersInWorkspaceContext(workspaceId), consolidationBatchId)
            .find(layer => readGridBatch(layer.params)?.sectionIndex === sectionIndex) ?? null
        );
        const paintedLayerIds: string[] = [];
        for (const section of touchedSections) {
          const existing = sectionLayerFor(section.index);
          const painted = await k.paintLayerIntoGridSection(task.layer, {
            workspaceId,
            section,
            layerId: existing?.id ?? null,
            params: existing?.params ?? {
              gridBatch: {
                batchId: consolidationBatchId,
                tileCount: consolidationTileCount ?? 1,
                completedTileIndexes: [],
                launch: layerConsolidation?.grid,
                sectionIndex: section.index,
              } satisfies GridBatch,
            },
            name: gridSections.sections.length > 1 ? `Grid ${section.index + 1}/${gridSections.sections.length}` : 'Grid',
          });
          if (painted) paintedLayerIds.push(painted.id);
        }
        // Recorded only once every section the tile touches holds it, so a resume never skips a half-painted tile.
        for (const layerId of paintedLayerIds) {
          const layer = k.getLayerRegistry()[layerId];
          const batch = layer ? readGridBatch(layer.params) : null;
          if (!layer || !batch) continue;
          const completedTileIndexes = [...new Set([...batch.completedTileIndexes, consolidationTileIndex])].sort((a, b) => a - b);
          k.setLayer(layerId, { ...layer, params: { ...layer.params, gridBatch: { ...batch, completedTileIndexes } } });
        }
        const batchLayers = findGridBatchLayers(k.getLayersInWorkspaceContext(workspaceId), consolidationBatchId);
        const doneTiles = new Set(batchLayers.flatMap(layer => readGridBatch(layer.params)?.completedTileIndexes ?? []));
        completedGridBatch = doneTiles.size >= (consolidationTileCount ?? 1);
        if (completedGridBatch) {
          batchLayers.forEach(layer => k.setLayer(layer.id, { ...layer, skipRender: undefined }));
        }
        const firstPainted = paintedLayerIds[0] ? k.getLayerRegistry()[paintedLayerIds[0]] : null;
        if (firstPainted) task.layer = firstPainted;
        createdVersionId = k.getWorkspace(workspaceId)?.activeVersionId ?? null;
        mergedIntoBatchAnchor = true;
        updateGenerationTaskSpawnTimelineEvent(task, {
          consolidatedIntoLayerId: paintedLayerIds[0] ?? null,
          consolidationBatchId,
        });
      }

      if (
        !gridSections &&
        hasBatchAnchorConsolidation &&
        !isConsolidationAnchorTile &&
        task.layer &&
        task.workspaceId &&
        kernel.current
      ) {
        const anchorLayer = findGridBatchAnchor(
          kernel.current.getLayersInWorkspaceContext(task.workspaceId),
          consolidationBatchId,
        );
        const anchorBatch = anchorLayer ? readGridBatch(anchorLayer.params) : null;

        if (anchorLayer && anchorBatch) {
          const completedTileIndexes = [...new Set([...anchorBatch.completedTileIndexes, consolidationTileIndex])]
            .sort((a, b) => a - b);
          completedGridBatch = completedTileIndexes.length >= anchorBatch.tileCount;
          const mergedLayer = await kernel.current.mergeGeneratedLayerIntoAnchorLayer(
            task.layer,
            anchorLayer.id,
            {
              // Grid consolidation is one automated batch. Keeping every tile merge out of the
              // global undo stack avoids retaining full-resolution pixel snapshots per tile;
              // ordinary user merges remain undoable through useLayers' default.
              description: `Merge grid tile ${consolidationTileIndex + 1}/${consolidationTileCount ?? '?'}`,
              isUndoable: false,
              params: { ...anchorLayer.params, gridBatch: { ...anchorBatch, completedTileIndexes } },
            }
          );
          if (mergedLayer) {
            const completedBatchLayer = completedGridBatch
              ? { ...mergedLayer, skipRender: undefined }
              : mergedLayer;
            if (completedBatchLayer !== mergedLayer) {
              kernel.current.setLayer(anchorLayer.id, completedBatchLayer);
            }
            task.layer = completedBatchLayer;
            // No tile after the anchor creates a version, so the active one is still the anchor's.
            createdVersionId = kernel.current.getWorkspace(task.workspaceId)?.activeVersionId ?? null;
            mergedIntoBatchAnchor = true;
            updateGenerationTaskSpawnTimelineEvent(task, {
              consolidatedIntoLayerId: anchorLayer.id,
              consolidationBatchId: consolidationBatchId,
            });
          }
        } else {
          console.warn('[useGenerationTasks] Missing batch anchor layer, falling back to default generation apply.', {
            taskId: task.id,
            consolidationBatchId,
            workspaceId: task.workspaceId,
          });
        }
      }

      if (serverMaterialized) {
        // The server already persisted this output; never upload, replace or resurrect it here.
      } else if (task.outputScope?.kind === 'PROJECT' && task.layer) {
        const projectLayer = materializeProjectGenerationLayer(task.layer, task.outputScope.projectId);
        task.layer = projectLayer;
        kernel.current?.setLayer(projectLayer.id, projectLayer);
        await persistGeneratedLayerAttach(
          projectLayer,
          task.outputScope.projectId,
          'useGenerationTasks.processGenerationTask.projectOutput',
          { throwOnError: true },
        );
      } else if (!mergedIntoBatchAnchor && task.layer && task.layerContextSnapshot && task.workspaceId && kernel.current) {
        const workspaceBefore = kernel.current.getWorkspace(task.workspaceId);
        if (!workspaceBefore) {
          throw new Error(`Workspace "${task.workspaceId}" not found when applying generation result.`);
        }
        const preWorkspaceLayerIds = [...(workspaceBefore.layerIds ?? [])];
        const preWorkspaceActiveVersionId = workspaceBefore.activeVersionId ?? null;
        const preWorkspaceLayers = preWorkspaceLayerIds
          .map((layerId) => kernel.current?.getLayerRegistry()[layerId])
          .filter((candidate): candidate is Layer => Boolean(candidate));
        const preVisibilityByLayerId = new Map<string, boolean>(
          preWorkspaceLayers.map((layer) => [layer.id, layer.visible !== false])
        );
        const preExistingNewLayer =
          (kernel.current.getLayerRegistry()[task.layer.id]) ?? null;
        const preExistingGeneratedAudioLayer = generatedAudioLayer
          ? (kernel.current.getLayerRegistry()[generatedAudioLayer.id] ?? null)
          : null;
        const newLayer = {
          ...(typeof task.layer.renderOrder === 'number' && Number.isFinite(task.layer.renderOrder)
            ? task.layer
            : {
                ...task.layer,
                renderOrder: getNextRenderOrderForWorkspace(preWorkspaceLayers, task.workspaceId),
              }),
          ...(isConsolidationAnchorTile ? {
            skipRender: true,
            params: {
              ...task.layer.params,
              gridBatch: {
                batchId: consolidationBatchId,
                tileCount: consolidationTileCount ?? 1,
                completedTileIndexes: [0],
                launch: layerConsolidation?.grid,
              } satisfies GridBatch,
            },
          } : {}),
        };
        if (generatedAudioLayer) {
          generatedAudioLayer = {
            ...generatedAudioLayer,
            renderOrder: (typeof newLayer.renderOrder === 'number' ? newLayer.renderOrder : preWorkspaceLayers.length) + 1,
          };
        }
        task.layer = newLayer;

        const taskCoversFullWorkspace = (() => {
          const taskRect = task.rect;
          const frameRect = workspaceBefore.frameRect;
          const tolerance = 1;
          return (
            Number.isFinite(taskRect.x) &&
            Number.isFinite(taskRect.y) &&
            Number.isFinite(taskRect.w) &&
            Number.isFinite(taskRect.h) &&
            Math.abs(taskRect.x - frameRect.x) <= tolerance &&
            Math.abs(taskRect.y - frameRect.y) <= tolerance &&
            Math.abs(taskRect.w - frameRect.w) <= tolerance &&
            Math.abs(taskRect.h - frameRect.h) <= tolerance
          );
        })();
        const layerContextVisibility = task.operation === 'vectorize-workspace' && !taskCoversFullWorkspace
          ? task.layerContextSnapshot.map((entry) => ({
              ...entry,
              visible: preVisibilityByLayerId.get(entry.id) ?? entry.visible,
            }))
          : task.layerContextSnapshot;

        const finalLayerVisibility = [
          ...layerContextVisibility,
          { id: newLayer.id, visible: newLayer.visible !== false },
          ...(generatedAudioLayer ? [{ id: generatedAudioLayer.id, visible: true }] : []),
        ];

        // This is the label the List tile shows. A wall-clock stamp ("Result 3:42:11 PM")
        // told the user nothing about which output they were looking at, and an upscale
        // carries no prompt to fall back on, so every one of them looked identical.
        const versionName = describeGeneratedResult(
          task,
          newLayer.type === 'video' ? 'video' : 'image',
        );
        const fallbackImageUrl = newLayer.type === 'video'
          ? (newLayer.videoFramesUrls?.find((url) => typeof url === 'string' && url.trim().length > 0) ?? null)
          : (newLayer.imageSrc || null);

        // A grid tile applies outside undo, where a hidden layer could never be shown again.
        let versionLayerVisibility = finalLayerVisibility;
        if (!hasBatchAnchorConsolidation) {
          try {
            versionLayerVisibility = await hideBuriedLayersInVisibility(kernel.current.buriedLayersWorkerRef, () => {
              const workspace = kernel.current?.getWorkspace(task.workspaceId);
              if (!kernel.current || !workspace) return null;
              const addedLayers = generatedAudioLayer ? [newLayer, generatedAudioLayer] : [newLayer];
              const addedIds = new Set(addedLayers.map((layer) => layer.id));
              const previousLayers = kernel.current.getLayersInWorkspaceContext(task.workspaceId).filter((layer) => !addedIds.has(layer.id));
              return { workspace, members: [...previousLayers, ...addedLayers] };
            }, finalLayerVisibility, newLayer.id);
          } catch (error) {
            console.error(`[Task ${task.id}] Hiding buried layers failed for workspace ${task.workspaceId}.`, error);
            kernel.current?.handleError('Could not hide the layers covered by others; the version is saved as is.');
          }
        }

        const versionDataForHook = {
          name: versionName,
          layerVisibility: versionLayerVisibility,
          parentId: task.parentVersionId || null,
        };

        // Create and render the new version immediately, awaiting its completion.
        throwIfGenerationTaskCancelled(task, abortSignal);
        const newVersion = await kernel.current.addCompletedGenerationAsVersion(
          task.workspaceId,
          versionDataForHook,
          {
            newLayerForRendering: newLayer,
            fallbackImageUrl,
            abortSignal,
            outputScope: task.outputScope,
          }
        );
        throwIfGenerationTaskCancelled(task, abortSignal);
        if (newVersion) createdVersionId = newVersion.id;

        // If version creation fails, we must stop and report the task as a failure.
        if (!newVersion || !newVersion.imageUrl) {
          throw new Error("Version creation failed: The version's preview image could not be generated.");
        }
        const taskProjectId = typeof task.projectId === 'string' ? task.projectId.trim() : '';
        if (!taskProjectId) {
          throw new Error(`Missing projectId on generation task "${task.id}" during layer apply.`);
        }
        throwIfGenerationTaskCancelled(task, abortSignal);

        const generationAction = {
          description: `Apply generation: ${versionName}`,
          redo: () => {
            const visibilityMap = new Map(newVersion.layerVisibility.map(item => [item.id, item.visible]));
            kernel.current?.setLayer(newLayer.id, newLayer);
            if (!preExistingNewLayer) {
              persistGeneratedLayerAttach(newLayer, taskProjectId, 'useGenerationTasks.processGenerationTask.redo');
            }
            if (generatedAudioLayer) {
              kernel.current?.setLayer(generatedAudioLayer.id, generatedAudioLayer);
              if (!preExistingGeneratedAudioLayer) {
                persistGeneratedLayerAttach(generatedAudioLayer, taskProjectId, 'useGenerationTasks.processGenerationTask.redoAudio');
              }
            }
            kernel.current?.setWorkspace(task.workspaceId, (workspace) => ({
              ...workspace,
              layerIds: [
                ...(workspace.layerIds ?? []).filter((layerId) => (
                  layerId !== newLayer.id && layerId !== generatedAudioLayer?.id
                )),
                newLayer.id,
                ...(generatedAudioLayer ? [generatedAudioLayer.id] : []),
              ],
              activeVersionId: newVersion.id,
            }), taskProjectId);

            const workspace = kernel.current?.getWorkspace(task.workspaceId);
            const workspaceLayerIds = workspace?.layerIds ?? [];
            workspaceLayerIds.forEach((layerId) => {
              const currentLayer = kernel.current?.getLayerRegistry()[layerId];
              if (!currentLayer) return;
              const nextVisible = visibilityMap.get(layerId) ?? false;
              if (currentLayer.visible === nextVisible) return;
              kernel.current?.setLayer(layerId, {
                ...currentLayer,
                visible: nextVisible,
              });
            });
          },
          undo: () => {
            kernel.current?.setWorkspace(task.workspaceId, (workspace) => ({
              ...workspace,
              layerIds: preWorkspaceLayerIds,
              activeVersionId: preWorkspaceActiveVersionId,
            }), taskProjectId);
            if (preExistingNewLayer) {
              kernel.current?.setLayer(newLayer.id, preExistingNewLayer);
            } else {
              kernel.current?.setLayer(newLayer.id, null);
              persistGeneratedLayerDetach(newLayer.id, 'useGenerationTasks.processGenerationTask.undo');
            }
            if (generatedAudioLayer) {
              if (preExistingGeneratedAudioLayer) {
                kernel.current?.setLayer(generatedAudioLayer.id, preExistingGeneratedAudioLayer);
              } else {
                kernel.current?.setLayer(generatedAudioLayer.id, null);
                persistGeneratedLayerDetach(generatedAudioLayer.id, 'useGenerationTasks.processGenerationTask.undoAudio');
              }
            }
            preWorkspaceLayerIds.forEach((layerId) => {
              const currentLayer = kernel.current?.getLayerRegistry()[layerId];
              if (!currentLayer) return;
              const previousVisible = preVisibilityByLayerId.get(layerId);
              if (previousVisible === undefined || currentLayer.visible === previousVisible) return;
              kernel.current?.setLayer(layerId, {
                ...currentLayer,
                visible: previousVisible,
              });
            });
          },
        };

        // The anchor tile is part of the automated grid batch. Execute its mutation without
        // registering the pixel-bearing action; manual generation keeps the normal undo path.
        if (hasBatchAnchorConsolidation) {
          generationAction.redo();
        } else {
          kernel.current.executeAction(generationAction);
        }
        // Written once the layer is in the registry: a runtime patch on an absent layer would create an id-less row.
        if (generationChange) kernel.current.patchEntityRuntimeState('StudioLayer', newLayer.id, { generationChange });

        if (generatedAudioLayer) {
          kernel.current.logUserAction('Mirelo SFX Track Created', {
            project_id: taskProjectId,
            workspace_id: task.workspaceId,
            carrier_video_layer_id: newLayer.id,
            audio_layer_id: generatedAudioLayer.id,
            timeline_start_ms: generatedAudioLayer.timelineStartMs ?? 0,
            timeline_end_ms: generatedAudioLayer.timelineEndMs ?? null,
          });
        }

        if (
          newLayer.name === "" &&
          (newLayer.imageData || (newLayer.type === 'video' && newLayer.videoFramesUrls && newLayer.videoFramesUrls.length > 0))
        ) {
          void fetchAndApplyLayerName(newLayer, task, abortSignal);
        }

        if (!mireloPostprocessFailure && kernel.current.getSpectacleMode()) {
          kernel.current.playUISound('SUCCESS', { volume: 0.4 });
        }

      }

      if (mireloPostprocessFailure) {
        throw mireloPostprocessFailure;
      }

      if (completedGridBatch && mergedIntoBatchAnchor && kernel.current?.getSpectacleMode()) {
        kernel.current.playUISound('SUCCESS', { volume: 0.4 });
      }

      if (kernel.current) {
        const nowIso = new Date().toISOString();
        const assetsToRegister: CloudAsset[] = [];
        const registerUrl = (url: string | null | undefined) => {
          const asset = buildCloudAssetEntry(url, 'generation', nowIso);
          if (asset) assetsToRegister.push(asset);
        };

        uploadedMediaUrls.forEach(registerUrl);

        const generatedLayer = task.layer;
        if (generatedLayer) {
          if (generatedLayer.type === 'video' && Array.isArray(generatedLayer.videoFramesUrls)) {
            registerUrl(generatedLayer.videoSrc);
            generatedLayer.videoFramesUrls.forEach(registerUrl);
          } else {
            registerUrl(generatedLayer.imageSrc);
          }
        }

        if (assetsToRegister.length > 0) {
          const existingRegistry = kernel.current.getCloudAssetRegistry() ?? [];
          const updatedRegistry = upsertCloudAssets(existingRegistry, assetsToRegister);
          kernel.current.setCloudAssetRegistry(() => updatedRegistry);
        }
      }

      // 1. Prepare the result data for any waiting promises (like in the CommandRunner).
      const resultData = {
          layer: compactTaskCompletionLayer(task.layer),
          urls: uploadedMediaUrls,
      };
      throwIfGenerationTaskCancelled(task, abortSignal);

      // 2. Signal the successful completion of the task. This resolves the `waitForTask` promise.
      if (kernel.current?.onTaskOutcome) {
          kernel.current.onTaskOutcome(task.id, 'SUCCESS', resultData);
      }

      // 3. Perform a single, consolidated state update for the completed task.
      setGenerationTasks(projectId, prevTasks =>
          prevTasks.map(t =>
              t.id === task.id
                  ? compactCompletedGenerationTask({
                      ...t,
                      isOutcomeReported: true, // Mark as reported to prevent duplicate signals.
                      urls: uploadedMediaUrls,
                      layer: task.layer,
                    })
                  : t
          )
      );

      if (task.layer?.id) {
        updateGenerationTaskSpawnTimelineEvent(task, {
          layerId: task.layer.id,
        });
      }

      if (!serverMaterialized && task.id && uploadedMediaUrls.length > 0) {
        void finalizeGenerationTaskSnapshot(task.serverTaskId, task.id, uploadedMediaUrls, {
          metadata: {
            layerId: task.layer?.id ?? null,
            workspaceId: task.workspaceId ?? null,
            chatSessionId: task.chatSessionId ?? null,
            projectId: projectId ?? null,
            taskType: task.type,
            model: task.model,
            layerConsolidation: task.layerConsolidation ?? null,
          },
          candidateVersionId: createdVersionId,
          chatSessionId: task.chatSessionId ?? null,
          outputScope: task.outputScope,
          workspaceId: task.workspaceId ?? null,
          projectId,
          layerId: task.layer?.id ?? null,
          generationLogId: dbJobId,
          providerJobId,
          taskStatus: 'MERGED',
        });
      }

      if (task.model === 'ideogram-inpaint' && task.workspaceId) {
        kernel.current?.setInpaintMaskShapes((previous) => {
          const currentWorkspaceMasks = previous[task.workspaceId];
          if (!currentWorkspaceMasks || currentWorkspaceMasks.length === 0) return previous;
          return {
            ...previous,
            [task.workspaceId]: [],
          };
        });
      }

      videoSocketTerminalEventsByTaskIdRef.current.delete(task.id);
      videoSocketTerminalWaitersByTaskIdRef.current.delete(task.id);

      // Release heavy local-only buffers after processing.
      delete task.mainImageData;
      delete task.maskFile;

      return uploadedMediaUrls;
    } catch (originalError: any) {
      if (originalError?.name === 'GenerationRecoveryUnavailableError' || originalError?.name === 'GenerationSubmissionUnconfirmedError') {
        handledGenerationTaskIdsRef.current.delete(task.id);
        setGenerationTasks(projectId, previous => previous.map(item => item.id === task.id
          ? compactCompletedGenerationTask({ ...item, error: originalError.message, recoveryPending: true }) : item));
        kernel.current?.handleError(originalError.message);
        kernel.current?.onTaskOutcome(task.id, 'FAILURE', undefined, Object.assign(originalError, { generationFailureReported: true }));
        delete task.mainImageData;
        delete task.maskFile;
        return [];
      }
      if (serverMaterialized && !isTaskCancellationError(originalError) && !abortSignal?.aborted) {
        handledGenerationTaskIdsRef.current.delete(task.id);
        kernel.current?.handleError('The generated result is saved but could not be loaded. Reconnect or refresh to load it; do not generate again.');
        setGenerationTasks(projectId, previous => previous.map(item => item.id === task.id
          ? compactCompletedGenerationTask(item) : item));
        delete task.mainImageData;
        delete task.maskFile;
        return [];
      }
      if (isTaskCancellationError(originalError) || abortSignal?.aborted) {
        const cancellationError = createTaskCancellationError(task.id, 'cancelled before completion.');
        setGenerationTasks(projectId, prevTasks => prevTasks.filter(t => t.id !== task.id));
        if (kernel.current?.onTaskOutcome) {
          kernel.current.onTaskOutcome(task.id, 'FAILURE', undefined, cancellationError);
        }
        void finalizeGenerationTaskSnapshot(task.serverTaskId, task.id, [], {
          metadata: {
            workspaceId: task.workspaceId ?? null,
            chatSessionId: task.chatSessionId ?? null,
            projectId: projectId ?? null,
            taskType: task.type,
            model: task.model,
          },
          chatSessionId: task.chatSessionId ?? null,
          workspaceId: task.workspaceId ?? null,
          projectId,
          generationLogId: dbJobId,
          providerJobId,
          outputScope: task.outputScope,
          taskStatus: 'ABORTED',
          failureMessage: cancellationError.message,
        });
        videoSocketTerminalEventsByTaskIdRef.current.delete(task.id);
        videoSocketTerminalWaitersByTaskIdRef.current.delete(task.id);
        delete task.mainImageData;
        delete task.maskFile;
        return [];
      }

      const detailedErrorMessage = getSpecificErrorMessage(originalError);
      const omniFriendlyErrorMessage =
        task.type === 'film' && task.model === 'kling-3-omni'
          ? extractKlingOmniFriendlyError(detailedErrorMessage)
          : null;
      if (omniFriendlyErrorMessage) {
        kernel.current?.handleError(omniFriendlyErrorMessage);
      }

      const terminalImageFailure = isTerminalImageGenerationFailure(originalError);
      const liveTask = kernel.current?.getGenerationTasks?.(projectId).find(candidate => candidate.id === task.id) ?? task;
      const recoveringFailure = Boolean(liveTask.listAttempt && listGenerationRecoveryStrategy(originalError));
      if (terminalImageFailure || recoveringFailure) {
        if (!recoveringFailure) {
          kernel.current?.handleError(detailedErrorMessage, { id: `generation-failure-${task.id}`, logToConsole: false });
        }
      } else if (axios.isAxiosError(originalError) && originalError.response?.status === 403) {
        kernel.current?.handleNotEnoughCreditsError();
      } else if (!omniFriendlyErrorMessage) {
        kernel.current?.handleError(detailedErrorMessage);
      }

      setGenerationTasks(projectId, prevTasks =>
        prevTasks.map(t => t.id === task.id
          ? compactCompletedGenerationTask({ ...t, error: detailedErrorMessage, failureCode: terminalImageGenerationFailureCode(originalError), failureDetails: readImageGenerationFailureDetails(originalError) })
          : t)
      );

      if (!terminalImageFailure && !recoveringFailure) console.error(`Error on ${task.type} task (ID: ${task.id}): ${detailedErrorMessage}. Original error:`, originalError);
      const propagatedError = Object.assign(new Error(detailedErrorMessage), { generationFailureReported: true, failureDetails: readImageGenerationFailureDetails(originalError) });
      propagatedError.name = originalError.name || 'ProcessingError';
      const terminalCode = terminalImageGenerationFailureCode(originalError);
      if (terminalCode) Object.assign(propagatedError, { code: terminalCode });

      if (axios.isAxiosError(originalError)) {
        const axiosError = originalError as AxiosError<any>;
        if (axiosError.code)             (propagatedError as any).code         = axiosError.code;
        if (axiosError.response?.status) (propagatedError as any).status       = axiosError.response.status;
        if (axiosError.response?.data)   (propagatedError as any).responseData = axiosError.response.data;
      }
      if (kernel.current?.onTaskOutcome) {
        kernel.current.onTaskOutcome(task.id, 'FAILURE', undefined, propagatedError);
      }

      // A recovered terminal payload comes from an authoritative SUCCEEDED
      // server snapshot. A transient browser/storage materialization failure
      // must not downgrade that durable success and make the paid asset
      // unrecoverable on the next reload.
      if (!serverMaterialized && !recoveredTerminal) {
        void finalizeGenerationTaskSnapshot(task.serverTaskId, task.id, [], {
          metadata: {
            workspaceId: task.workspaceId ?? null,
            chatSessionId: task.chatSessionId ?? null,
            projectId: projectId ?? null,
            taskType: task.type,
            model: task.model,
          },
          chatSessionId: task.chatSessionId ?? null,
          workspaceId: task.workspaceId ?? null,
          projectId,
          generationLogId: dbJobId,
          providerJobId,
          outputScope: task.outputScope,
          taskStatus: 'FAILED',
          failureMessage: detailedErrorMessage,
        });
      }

      videoSocketTerminalEventsByTaskIdRef.current.delete(task.id);
      videoSocketTerminalWaitersByTaskIdRef.current.delete(task.id);

      // Release heavy local-only buffers even on failure.
      delete task.mainImageData;
      delete task.maskFile;
      
      return [];
    }
  }, [processRepaintOnMask, kernel, fetchAndApplyLayerName, processStretchOnArea, finalizeRasterLayerGeometry, persistGeneratedLayerAttach, persistGeneratedLayerDetach, setGenerationTasks, emitGenerationTaskSpawnTimelineEvent, updateGenerationTaskSpawnTimelineEvent, throwIfGenerationTaskCancelled, waitForVideoSocketTerminal, runGenerationPostprocess]);

  useEffect(() => {
    const recoveringTaskIds = new Set<string>();
    recoverCompletedFilmTaskRef.current = (task, payload) => {
      if (recoveringTaskIds.has(task.id)) return;
      recoveringTaskIds.add(task.id);
      void processGenerationTask(task, undefined, payload).finally(() => {
        recoveringTaskIds.delete(task.id);
      });
    };
    return () => {
      recoverCompletedFilmTaskRef.current = null;
    };
  }, [processGenerationTask]);

  const processTasks = useCallback(async (tasks: GenerationTask[], options?: { abortSignal?: AbortSignal }) => {
    if (!tasks || tasks.length === 0) {
      console.warn("processTasks called with an empty tasks array.");
      return;
    }

    // Capture the destination at this single launch door. Every later merge/finalization reads
    // this value, never the workspace or project currently visible in the UI.
    const launchTasks = tasks.filter((task) => {
      const outputScope = normalizeGenerationOutputScope(task.outputScope, {
        workspaceId: task.workspaceId,
        projectId: task.projectId,
      });
      const projectId = typeof task.projectId === 'string' ? task.projectId.trim() : '';
      if (!outputScope || !projectId) {
        console.warn(`[useGenerationTasks] Skipping task with an invalid output scope: ${task.id}`);
        return false;
      }
      if (outputScope.kind === 'PROJECT') {
        if (outputScope.projectId !== projectId) {
          console.warn(`[useGenerationTasks] Skipping task outside its project output scope: ${task.id}`);
          return false;
        }
        task.outputScope = outputScope;
        return true;
      }
      if (task.workspaceId?.trim() !== outputScope.workspaceId) {
        console.warn(`[useGenerationTasks] Skipping task with an incoherent output scope: ${task.id}`);
        return false;
      }
      const workspace = kernel.current?.getWorkspace(outputScope.workspaceId);
      if (!workspace || workspace.projectId !== projectId) {
        console.warn(`[useGenerationTasks] Skipping task whose output workspace is outside its project: ${task.id}`);
        return false;
      }
      task.outputScope = outputScope;
      return true;
    });
    if (launchTasks.length === 0) return;

    // Stamp the start once, here, because this is the single door every generation comes
    // through. The pending tile shows elapsed time, and timing it from when the tile mounted
    // understated a job by minutes whenever the user switched view and came back.
    const startedAt = Date.now();
    launchTasks.forEach((task) => { if (typeof task.startedAt !== 'number') task.startedAt = startedAt; });
    const tasksByProjectId = new Map<string, GenerationTask[]>();
    launchTasks.forEach((task) => {
      const projectId = typeof task.projectId === 'string' ? task.projectId.trim() : '';
      if (!projectId) {
        console.warn(`[useGenerationTasks] Skipping task without projectId: ${task.id}`);
        return;
      }
      const existing = tasksByProjectId.get(projectId);
      if (existing) {
        existing.push(task);
      } else {
        tasksByProjectId.set(projectId, [task]);
      }
    });
    tasksByProjectId.forEach((projectTasks, projectId) => {
      // Replace a retry's presentation atomically; keep every attempt in the audit history.
      const retryRoots = new Set(projectTasks.map(task => task.listAttempt?.rootTaskId).filter(Boolean));
      setGenerationTasks(projectId, prevTasks => [
        ...prevTasks.map(task => task.error && retryRoots.has(task.listAttempt?.rootTaskId ?? task.id)
          ? { ...task, isOutcomeReported: true } : task),
        ...projectTasks,
      ]);
      const pendingVideoTracksByWorkspaceId = new Map<string, PendingVideoGenerationTrack[]>();
      projectTasks.forEach((task) => {
        const pendingTrack = resolvePendingVideoGenerationTrack(task);
        if (!pendingTrack) return;
        const workspaceId = normalizeTimelineString(task.workspaceId);
        if (!workspaceId) return;
        const existing = pendingVideoTracksByWorkspaceId.get(workspaceId);
        if (existing) {
          existing.push(pendingTrack);
        } else {
          pendingVideoTracksByWorkspaceId.set(workspaceId, [pendingTrack]);
        }
      });
      pendingVideoTracksByWorkspaceId.forEach((pendingTracks, workspaceId) => {
        if (pendingTracks.length === 0) return;
        const maxPendingEndMs = pendingTracks.reduce(
          (max, track) => Math.max(max, track.endMs),
          0
        );
        kernel.current?.setWorkspace(workspaceId, (workspace) => {
          const currentDurationMs = Math.max(0, toFiniteNumber(workspace.timelineDurationMs) ?? 0);
          const currentCursorMs = Math.max(0, toFiniteNumber(workspace.timelineCursorMs) ?? 0);
          const nextDurationMs = Math.max(currentDurationMs, maxPendingEndMs);
          return {
            ...workspace,
            timelineDurationMs: nextDurationMs > 0 ? nextDurationMs : workspace.timelineDurationMs,
            timelineCursorMs: Math.min(currentCursorMs, Math.max(0, nextDurationMs - 1)),
            preferVersionPanelOverTimeline: false,
            imageTimelineManuallyEnabled: true,
          };
        }, projectId);
      });
    });
    kernel.current?.setIsSubmittingAITask(false);

    try {
      await Promise.allSettled(tasks.map((task) => processGenerationTask(task, options?.abortSignal)));
    } catch (error) {
      // This catch block might not be strictly necessary with Promise.allSettled,
      // but good for catching potential setup errors before the loop.
      console.error("Unexpected error processing tasks batch:", error);
    }
  }, [kernel, processGenerationTask, setGenerationTasks]);

  const runGridBatch = useCallback<KernelHandle['runGridBatch']>(async (workspaceId, script, batchId, sessionId) => {
    const k = kernel.current;
    if (!k) return;
    try {
      await k.runGeneratedScriptLogic(workspaceId, script, undefined, {
        sessionId: sessionId || k.getActiveChatSessionId() || undefined,
      });
    } finally {
      findGridBatchLayers(k.getLayersInWorkspaceContext(workspaceId), batchId)
        .filter(layer => layer.skipRender)
        .forEach(layer => k.setLayer(layer.id, { ...layer, skipRender: undefined }));
    }
  }, [kernel]);

  const resumeGridBatch = useCallback<KernelHandle['resumeGridBatch']>(async (layerId) => {
    const k = kernel.current;
    const anchor = k?.getLayerRegistry()[layerId];
    const workspaceId = anchor?.layerWorkspaceId;
    if (!k || !anchor || !workspaceId) return;
    const workspaceLayers = k.getLayersInWorkspaceContext(workspaceId);
    const batch = readResumableGridBatch(anchor, workspaceLayers);
    if (!batch) return;
    const batchLayers = findGridBatchLayers(workspaceLayers, batch.batchId);
    const unloaded = batchLayers.filter(layer => !layer.imageData);
    if (unloaded.length > 0) {
      unloaded.forEach(layer => k.reloadLayerAsset(layer.id));
      throw new Error('The grid layers are still loading. Resume again once they are visible.');
    }
    const { baseRect, runtime, modelId, modelPreset, modelParams, keepSelectedModel } = batch.launch;
    if (modelParams && !keepSelectedModel) {
      k.setModelParams(prev => ({ ...prev, [modelId]: modelParams as unknown as DiffusionParams }));
    }
    const script = buildGridScript(baseRect, runtime, modelId, modelPreset, {
      batchId: batch.batchId,
      modelParams: modelParams ?? null,
      skipTileIndexes: batch.completedTileIndexes,
      sections: batch.launch.sections ?? null,
      keepSelectedModel,
    });
    batchLayers.forEach(layer => k.setLayer(layer.id, { ...layer, skipRender: true }));
    k.logUserAction('Grid Batch Resumed', {
      layer_id: anchor.id,
      workspace_id: workspaceId,
      completed_tiles: batch.completedTileIndexes.length,
      tile_count: batch.tileCount,
      model_id: modelId,
    });
    await k.addAssistantMessage(
      `Resuming grid: ${batch.tileCount - batch.completedTileIndexes.length} of ${batch.tileCount} tiles left.`,
      [script],
      'Fater',
    );
    await runGridBatch(workspaceId, script, batch.batchId);
  }, [kernel, runGridBatch]);

  return {
    nameListOutputs,
    pendingTaskPromisesRef,
    waitForTask,
    reportTaskSuccess,
    reportTaskFailure,
    onTaskOutcome,
    getGenerationTasks,
    setGenerationTasks,
    cancelGenerationTasksForSession,
    handleGenerationTaskStatusSocketEvent,
    reconcilePendingGenerationTasks,
    dismissFailedGenerationTask,
    processTasks,
    runGridBatch,
    resumeGridBatch,
  };
};

export default useGenerationTasks;
